// @ts-nocheck
/**
 * CreditBlockClientDetail — Refactored to use DynamicTabRenderer
 * 
 * This version uses the same layout system as CyberSweep client detail:
 * - DynamicTabRenderer fetches Record Page config from Gobodo (or mock)
 * - Tabs, sections, and fields are configured via Object Manager
 * - Specialized components (bureaus, history, files) are rendered within the layout
 */
import React, { useState, useEffect, useRef, useMemo, useCallback, useReducer } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Shield,
  Save,
  X,
  Plus,
  Download,
  Upload,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  FileText,
  Edit3,
  Eye,
  EyeOff,
  Clock,
  Settings,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import {
  CreditBlockClient,
  CreditBlockFile,
  CreditBlockHistoryEntry,
  CreditBlockProduct,
  CreditReportOrder,
  CreditReportRecord,
  type BureauCredential,
} from '../../types';
import type { ListViewRecord } from '../../types/listView';
import type { User } from '../../types/user';
import { creditReportService } from '../../services/creditReportService';
import CreditReportOrderWidget from '../../components/common/CreditReportOrderWidget';
import ArrayCreditPullPanel from '../../components/common/ArrayCreditPullPanel';
import FileViewerModal from '../../components/common/FileViewerModal';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import { isArrayCreditPullEnabled } from '../../services/featureFlagService';
import { useCreditReportRecords } from '../../services/queries/creditReportQueries';
import {
  useCreditBlockBureaus,
  useCreateUnlockRequest,
  useUpdateBureauField,
  useSaveBureauCredentials,
  useUpdateBureauStages,
} from '../../services/queries/creditBlockBureauQueries';
import { useCreditBlockSubscriptions, useUpdateCreditBlockSubscriptionField } from '../../services/queries/creditBlockSubscriptionQueries';
import { useCreditBlockHistory, useCreditBlockUnlockRequests } from '../../services/queries/creditBlockHistoryQueries';
import { useCreditBlockFiles, useUploadCreditBlockFile, useCreditBlockFileCounts, getFileForDownload, getRelTypeKeyForUpload, type CreditBlockFileCategory } from '../../services/queries/creditBlockFileQueries';
import { useSalesforceFiles, downloadSalesforceFile, type SalesforceFile } from '../../services/queries/salesforceFileQueries';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { useCreditBlockCases } from '../../services/queries/creditBlockCaseQueries';
import { useCreditBlockActivitiesForCases } from '../../services/queries/creditBlockActivityQueries';
import { StandardButton, StandardSelect } from '../../components/standard';
import { BUREAU_LOGOS, getBureauLogo } from '../../constants/bureauLogos';
import DynamicTabRenderer from '../../components/dynamic/DynamicTabRenderer';
import DynamicSectionRenderer from '../../components/dynamic/DynamicSectionRenderer';
import SalesforceRelatedList from '../../components/credit-block/SalesforceRelatedList';
import type { RelatedListColumn } from '../../components/credit-block/SalesforceRelatedList';
import BureauDetailView from '../../components/credit-block/BureauDetailView';
import CaseDetailView from '../../components/credit-block/CaseDetailView';
import HistoryDetailView from '../../components/credit-block/HistoryDetailView';
import CreditReportDetailView from '../../components/credit-block/CreditReportDetailView';
import SalesforceActivityTab from '../../components/credit-block/SalesforceActivityTab';
import NewTaskModal from '../../components/credit-block/NewTaskModal';
import { useCreateActivity } from '../../hooks/useActivities';
import { getCustomSuffix } from '../../config/gobodoConfig';
import { useApplicationPageConfig } from '../../services/queries/configurationQueries';
import { useUpdateCreditBlockClientNotes } from '../../services/queries/creditBlockQueries';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { fetchUsersForUserManagement, userKeys } from '../../services/queries/userQueries';
import { usersToCreditBlockAssigneeOptions } from '../../services/gobodo/creditBlockNewCaseGobodoSearch';
import {
  CREDIT_BLOCK_ASSIGNEE_USER_FILTERS,
  encodeNameToken,
  isCreditBlockTeamUser,
  ownerSelectOptionsForAssignee,
} from '../../services/creditBlock/creditBlockAssigneePickerUtils';
import { createRecord, updateRecord } from '../../services/recordStorageService';
import { buildCreditBlockLookupSavePayload } from './creditBlockLookupSave';
import {
  buildCreditBlockCaseCreatePayload,
  graphKeyFromCreatedRecord,
} from '../../services/creditBlock/creditBlockCaseCreate';
import { CREDIT_BLOCK_SYSTEM_CASE_ORIGIN } from '../../services/creditBlock/caseOriginDisplay';
import {
  applyCreditBlockRecordTypeFields,
  findCreditBlockRecordTypeTarget,
} from '../../services/creditBlock/creditBlockCaseGraphRelations';

import { useSendEmail } from '../../hooks/useSendEmail';
import { useCanEdit } from '../../services/queries/permissionQueries';
import {
  BUREAUS,
  BUREAU_LABELS,
  CORE_THREE_BUREAUS,
  isLockedOrBlockedBureau,
  isUnblockedBureau,
  normalizeOwnerLabel,
  stripGraphSuffix,
  type NewBureauRequestFormKind,
} from './creditBlockClientConstants';
import {
  formatSubDate,
  normalizeDateForInput,
  normalizeDisplayToken,
  subscriptionDisplayLabel,
  subscriptionStageLabel,
} from './creditBlockSubscriptionDisplay';
import { CreditBlockSubscriptionWorkspaceTabContent } from './CreditBlockSubscriptionWorkspaceTabContent';
import {
  BlockBureausModal,
  ExtensionBureausModal,
  FirstTimeBlockOwnerModal,
  PermanentUnblockOwnerModal,
  UnblockBureausModal,
} from './creditBlockToolbar/CreditBlockBureauToolModals';
import { useCreditBlockToolbarFlows, resolveEndDateFromLength } from './hooks/useCreditBlockToolbarFlows';
import { useCreditBlockClientDetailUrl } from './hooks/useCreditBlockClientDetailUrl';

/** Stable fallback so `useQuery` undefined data does not produce a new `[]` every render (avoids effect loops). */
const EMPTY_OWNER_USERS_LIST: User[] = [];

interface CreditBlockClientDetailProps {
  client: CreditBlockClient;
  onBack: () => void;
  onPullCreditReport?: (client: CreditBlockClient) => void;
  onOpenClientDetails?: (clientLookup: string) => void;
}

const CreditBlockClientDetail: React.FC<CreditBlockClientDetailProps> = ({ client, onBack, onPullCreditReport, onOpenClientDetails }) => {
  const [clientEditModalOpen, setClientEditModalOpen] = useState(false);
  const [clientEditValues, setClientEditValues] = useState<Record<string, string>>({});
  const [clientEditSaving, setClientEditSaving] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempEditValue, setTempEditValue] = useState<string>('');
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>(
    Object.fromEntries(BUREAUS.map((b) => [b, false]))
  );
  const [formData, setFormData] = useState(() => {
    const bc = client.bureauCredentials ?? {};
    return {
      ...client,
      bureauCredentials: {
        chexsystems: bc.chexsystems ?? {},
        equifax: bc.equifax ?? {},
        experian: bc.experian ?? {},
        transunion: bc.transunion ?? {},
      },
    };
  });

  const [selectedCRRecord, setSelectedCRRecord] = useState<CreditReportRecord | null>(null);
  const [files, setFiles] = useState<CreditBlockFile[]>(client.files ?? []);
  const [fileCategoryFilter, setFileCategoryFilter] = useState<string>('all');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  type BureauChoice = 'equifax' | 'experian' | 'transunion' | 'chexsystems' | 'all_three';
  const [newRequestForm, setNewRequestForm] = useState({
    bureau: 'equifax' as BureauChoice,
    requestType: 'unlock_request' as NewBureauRequestFormKind,
    reason: '',
    startDate: '',
    endDate: '',
  });
  const [requestTypeFilter, setRequestTypeFilter] = useState<
    | 'all'
    | 'unlock_request'
    | 'lock_request'
    | 'permanent_block'
    | 'permanent_unblock'
    | 'extension'
    | 'special_case'
  >('all');
  const [selectedRequest, setSelectedRequest] = useState<CreditBlockHistoryEntry | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<string>('cb_account_detail');
  const [tabNavigationRequest, setTabNavigationRequest] = useState<{ tabId: string; nonce: number } | null>(null);
  /** Selected CreditBlock__c row for Subscription tab (SF subscription record view). */
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | null>(null);
  /** Selected bureau key for the SF-style bureau detail drill-in (e.g. 'equifax'). */
  const [selectedBureauKey, setSelectedBureauKey] = useState<string | null>(null);
  /** Selected case record for the SF-style case detail drill-in. */
  const [selectedCaseRecord, setSelectedCaseRecord] = useState<ListViewRecord | null>(null);
  /** Selected history entry for the SF-style history detail drill-in. */
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState<CreditBlockHistoryEntry | null>(null);
  /** New task creation modal. */
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showClientActivityModal, setShowClientActivityModal] = useState(false);
  const [clientActivityModalType, setClientActivityModalType] = useState<'meeting' | 'call' | 'note' | 'email'>('note');
  const [clientActivitySavedAt, setClientActivitySavedAt] = useState(0);
  const [localCreditPullOpen, setLocalCreditPullOpen] = useState(false);
  const { userId, userName } = useCurrentUser();
  const { createActivity: createClientActivity } = useCreateActivity();
  type ToolbarBureauModalKey = 'firstTime' | 'perm' | 'unblock' | 'extension' | 'block';
  const toolbarModalsInitial = {
    firstTime: false,
    perm: false,
    unblock: false,
    extension: false,
    block: false,
  } as const;
  const toolbarModalsReducer = (
    state: typeof toolbarModalsInitial,
    action: { type: 'open'; key: ToolbarBureauModalKey } | { type: 'close'; key: ToolbarBureauModalKey }
  ): typeof toolbarModalsInitial => {
    if (action.type === 'open') {
      return { firstTime: false, perm: false, unblock: false, extension: false, block: false, [action.key]: true };
    }
    return { ...state, [action.key]: false };
  };
  const [toolbarModals, dispatchToolbarModal] = useReducer(toolbarModalsReducer, toolbarModalsInitial);
  const [selectedCaseOwner, setSelectedCaseOwner] = useState<string>(client.accountOwner || '');
  const [permRequestSource, setPermRequestSource] = useState<string>('');
  const [permSelectedBureaus, setPermSelectedBureaus] = useState<Record<string, boolean>>({
    chexsystems: false,
    equifax: true,
    experian: true,
    transunion: true,
  });
  const [unblockRequestSource, setUnblockRequestSource] = useState<string>('');
  const [unblockStartDate, setUnblockStartDate] = useState<string>('');
  const [unblockEndDate, setUnblockEndDate] = useState<string>('');
  const [unblockEndLength, setUnblockEndLength] = useState<string>('');
  const [unblockSelectedBureaus, setUnblockSelectedBureaus] = useState<Record<string, boolean>>({
    chexsystems: false,
    equifax: false,
    experian: false,
    transunion: false,
  });
  const [extensionRequestSource, setExtensionRequestSource] = useState<string>('');
  const [extensionStartDate, setExtensionStartDate] = useState<string>('');
  const [extensionEndDate, setExtensionEndDate] = useState<string>('');
  const [extensionEndLength, setExtensionEndLength] = useState<string>('');
  const [extensionSelectedBureaus, setExtensionSelectedBureaus] = useState<Record<string, boolean>>({
    chexsystems: false,
    equifax: false,
    experian: false,
    transunion: false,
  });
  const [blockRequestSource, setBlockRequestSource] = useState<string>('');
  const [blockSelectedBureaus, setBlockSelectedBureaus] = useState<Record<string, boolean>>({
    chexsystems: false,
    equifax: false,
    experian: false,
    transunion: false,
  });

  const { clientSection, searchParams, navigate, syncTabSlugInUrl } = useCreditBlockClientDetailUrl({
    clientId: client.id,
    setActiveTab,
    setTabNavigationRequest,
    setSelectedSubscriptionId,
  });

  const creditBlockFirstTabActive =
    activeTab === 'cb_account_detail' ||
    activeTab === 'cb_client_details_tab' ||
    activeTab === 'details';
  const subscriptionWorkspaceActive = activeTab === 'cb_subscription_workspace';

  // Lazy-load queries — only fire when the relevant tab is active
  // First tab loads subscriptions + history related lists (merged into overview)
  const { data: gobodoCRRecords = [], isLoading: crRecordsLoading, isError: crRecordsError } = useCreditReportRecords(
    client.id,
    client.accountName,
    activeTab === 'credit-report'
  );
  const { data: gobodoBureaus, isLoading: bureausLoading, isError: bureausError, refetch: refetchBureaus } = useCreditBlockBureaus(
    client.id,
    subscriptionWorkspaceActive,
    client.bureauCredentials
  );
  const { data: gobodoHistory = [], isLoading: historyLoading, isError: historyError, refetch: refetchHistory } = useCreditBlockHistory(
    client.id,
    creditBlockFirstTabActive || activeTab === 'lock-requests' || subscriptionWorkspaceActive
  );
  const { data: allUnlockRequests = [] } = useCreditBlockUnlockRequests(
    activeTab === 'lock-requests'
  );
  const clientCaseLookupIds = useMemo(() => {
    const raw = [
      String(client.id ?? '').trim(),
      String((client as any).sf_id ?? '').trim(),
      String((client as any).salesforce_id ?? '').trim(),
      String(client.arrayInfo?.clientKey ?? '').trim(),
      stripGraphSuffix(String(client.id ?? '').trim()),
    ];
    const seen = new Set<string>();
    const deduped: string[] = [];
    for (const id of raw.filter(Boolean)) {
      const key = stripGraphSuffix(String(id).trim()).toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      deduped.push(id);
    }
    return deduped;
  }, [client.id, client.arrayInfo?.clientKey, (client as any).sf_id, (client as any).salesforce_id]);
  const { data: creditBlockCases = [], isLoading: casesLoading, refetch: refetchCases } = useCreditBlockCases(
    clientCaseLookupIds,
    subscriptionWorkspaceActive,
    { knownClient: { id: client.id, accountName: client.accountName } }
  );
  const creditBlockCaseLookups = useMemo(() => {
    const suffix = getCustomSuffix();
    const out = new Set<string>();
    for (const row of creditBlockCases) {
      const rawCandidates = [
        String((row as any).key ?? '').trim(),
        String((row as any).id ?? '').trim(),
        String((row as any).sf_id ?? '').trim(),
        String((row as any).salesforce_id ?? '').trim(),
        String((row as any).caseId ?? '').trim(),
      ].filter(Boolean);
      for (const candidate of rawCandidates) {
        out.add(candidate);
        const stripped = stripGraphSuffix(candidate);
        if (stripped) {
          out.add(stripped);
          if (suffix && !stripped.toLowerCase().endsWith(suffix.toLowerCase())) {
            out.add(`${stripped}${suffix}`);
          }
        }
      }
    }
    return Array.from(out);
  }, [creditBlockCases]);
  const {
    data: creditBlockActivities = [],
    isLoading: creditBlockActivitiesLoading,
    error: creditBlockActivitiesError,
  } = useCreditBlockActivitiesForCases(
    creditBlockCaseLookups,
    activeTab === 'activity' && creditBlockCaseLookups.length > 0
  );
  const { data: gobodoFiles = [], isLoading: filesLoading, isError: filesError } = useCreditBlockFiles(
    client.id,
    activeTab === 'files',
    fileCategoryFilter as CreditBlockFileCategory
  );
  const { data: sfFiles = [], isLoading: sfFilesLoading } = useSalesforceFiles(client.id, activeTab === 'files');
  const { data: fetchedSubscriptions, isLoading: subscriptionsLoading, refetch: refetchSubscriptions } = useCreditBlockSubscriptions(
    client.id,
    creditBlockFirstTabActive || subscriptionWorkspaceActive,
    client.assignedProducts
  );
  const subscriptionRows = USE_GOBODO_API ? (fetchedSubscriptions ?? client.assignedProducts) : client.assignedProducts;
  const uploadFileMutation = useUploadCreditBlockFile(client.id);
  const updateBureau = useUpdateBureauField(client.id);
  const updateBureauStages = useUpdateBureauStages(client.id);
  const [caseCreating, setCaseCreating] = useState(false);
  const [activeToolbarSubmit, setActiveToolbarSubmit] = useState<
    'lock_request' | 'unlock_request' | 'permanent_unblock' | 'extension' | null
  >(null);
  /** Step-by-step progress for toolbar / first-time flows (cleared when `caseCreating` ends). */
  const [toolbarFlowProgress, setToolbarFlowProgress] = useState<{
    flowTitle: string;
    phase: string;
    bureau?: string;
    step?: number;
    total?: number;
  } | null>(null);
  const sendAckEmail = useSendEmail();
  const saveBureauCredentials = useSaveBureauCredentials(client.id);
  const createUnlockReq = useCreateUnlockRequest(client.id);
  const updateSubscriptionField = useUpdateCreditBlockSubscriptionField();
  const { data: fileCounts } = useCreditBlockFileCounts(client.id, USE_GOBODO_API && activeTab === 'files');
  const { canEdit: canEditCreditBlock } = useCanEdit('credit_block');
  const [mockCRRecords, setMockCRRecords] = useState<CreditReportRecord[]>([]);


  const clientCRRecords = USE_GOBODO_API ? gobodoCRRecords : mockCRRecords;
  const bureauCredentials = useMemo(
    () =>
      USE_GOBODO_API && gobodoBureaus
        ? gobodoBureaus
        : (client.bureauCredentials ?? { chexsystems: {}, equifax: {}, experian: {}, transunion: {} }),
    [gobodoBureaus, client.bureauCredentials]
  );
  const historyEntries = useMemo(() => {
    if (!USE_GOBODO_API) return client.history ?? [];
    if (gobodoHistory.length > 0) return gobodoHistory;
    if (activeTab !== 'lock-requests') return [];
    if (allUnlockRequests.length === 0) return [];

    const normalizeToken = (raw: unknown) =>
      String(raw ?? '')
        .trim()
        .replace(/__stg3_c$|__stg_c$|__dev_c$|__stg$|__c$/i, '')
        .toLowerCase();
    const tokenSet = new Set(
      [
        client.id,
        client.arrayInfo?.clientKey,
        (client as any).sf_id,
        (client as any).salesforce_id,
      ]
        .map(normalizeToken)
        .filter(Boolean)
    );
    const nameToken = String(client.accountName ?? '').trim().toLowerCase();
    return allUnlockRequests.filter((row) => {
      const rowClientToken = normalizeToken((row as any).clientId);
      const rowNameToken = String((row as any).clientName ?? '').trim().toLowerCase();
      if (rowClientToken && tokenSet.has(rowClientToken)) return true;
      if (nameToken && rowNameToken && rowNameToken === nameToken) return true;
      return false;
    });
  }, [allUnlockRequests, client, gobodoHistory, activeTab]);
  const lockUnlockEntries = useMemo(
    () =>
      historyEntries.filter((e) =>
        [
          'lock_request',
          'unlock_request',
          'permanent_block',
          'permanent_unblock',
          'extension',
          'special_case',
        ].includes(e.type)
      ),
    [historyEntries]
  );
  const filteredLockRequests = useMemo(() => {
    if (requestTypeFilter === 'all') return lockUnlockEntries;
    return lockUnlockEntries.filter((r) => r.type === requestTypeFilter);
  }, [lockUnlockEntries, requestTypeFilter]);
  const { data: ownerUsersData } = useQuery({
    queryKey: userKeys.list(CREDIT_BLOCK_ASSIGNEE_USER_FILTERS),
    queryFn: () => fetchUsersForUserManagement(CREDIT_BLOCK_ASSIGNEE_USER_FILTERS),
    staleTime: 5 * 60 * 1000,
  });
  const ownerUsers = ownerUsersData ?? EMPTY_OWNER_USERS_LIST;
  const caseOwnerOptions = useMemo(() => {
    const raw = [client.accountOwner, client.accountManager, client.dayToDayContact].filter(Boolean) as string[];
    const unique = Array.from(new Set(raw.map((s) => s.trim()).filter(Boolean)));
    const labels = Array.from(new Set(['Credit Block', ...unique]));
    return labels.map((name) => ({ value: encodeNameToken(name), label: name }));
  }, [client.accountOwner, client.accountManager, client.dayToDayContact]);
  const workflowCaseOwnerOptions = useMemo(() => {
    const scopedUsers = ownerUsers.filter(isCreditBlockTeamUser);
    const baseUsers = scopedUsers.length > 0 ? scopedUsers : ownerUsers;
    const opts = ownerSelectOptionsForAssignee(
      usersToCreditBlockAssigneeOptions(baseUsers),
      userName
    );
    const creditBlockOption = { value: encodeNameToken('Credit Block'), label: 'Credit Block' };
    if (opts.length > 0) {
      const hasCreditBlock = opts.some((o) => normalizeOwnerLabel(o.label) === normalizeOwnerLabel('Credit Block'));
      return hasCreditBlock ? opts : [creditBlockOption, ...opts];
    }
    return caseOwnerOptions;
  }, [ownerUsers, userName, caseOwnerOptions]);

  useEffect(() => {
    if (USE_GOBODO_API && gobodoBureaus) {
      setFormData((prev) => ({ ...prev, bureauCredentials: gobodoBureaus }));
    }
  }, [gobodoBureaus]);

  useEffect(() => {
    const clientOwnerLabel = String(client.accountOwner ?? '').trim();
    const creditBlockDefault =
      workflowCaseOwnerOptions.find(
        (option) => normalizeOwnerLabel(option.label) === normalizeOwnerLabel('Credit Block')
      )?.value ?? encodeNameToken('Credit Block');
    const matchedOwner = clientOwnerLabel
      ? workflowCaseOwnerOptions.find(
          (option) => normalizeOwnerLabel(option.label) === normalizeOwnerLabel(clientOwnerLabel)
        ) ?? workflowCaseOwnerOptions[0]
      : workflowCaseOwnerOptions[0];
    setSelectedCaseOwner(matchedOwner?.value || creditBlockDefault);
    setPermRequestSource('');
    setPermSelectedBureaus({
      chexsystems: false,
      equifax: true,
      experian: true,
      transunion: true,
    });
    setUnblockRequestSource('');
    setUnblockStartDate('');
    setUnblockEndLength('');
    setUnblockSelectedBureaus({
      chexsystems: false,
      equifax: false,
      experian: false,
      transunion: false,
    });
    setBlockRequestSource('');
    setBlockSelectedBureaus({
      chexsystems: false,
      equifax: false,
      experian: false,
      transunion: false,
    });
  }, [client.id, client.accountOwner, workflowCaseOwnerOptions]);

  useEffect(() => {
    if (USE_GOBODO_API) return;
    const loadCRRecords = () => setMockCRRecords(creditReportService.getClientCRRecords(client.id));
    loadCRRecords();
    return creditReportService.subscribeToCRRecords(loadCRRecords);
  }, [client.id]);

  useEffect(() => {
    if (!USE_GOBODO_API) setFiles(client.files ?? []);
  }, [client.id, client.files]);

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => setToast({ message, type });

  useEffect(() => {
    if (uploadFileMutation.data) showToast('File uploaded successfully');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileMutation.data]);

  useEffect(() => {
    if (uploadFileMutation.error) showToast('File failed to upload', 'error');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileMutation.error]);

  useEffect(() => {
    if (!creditBlockActivitiesError || activeTab !== 'activity') return;
    showToast(
      creditBlockActivitiesError instanceof Error
        ? creditBlockActivitiesError.message
        : 'Unable to load client activity feed',
      'error'
    );
  }, [creditBlockActivitiesError, activeTab]);
  const handleCreditBlockSettingsClick = () => {
    showToast('Credit Block settings panel is coming soon.');
  };
  const refreshCreditBlockOverview = async () => {
    await Promise.allSettled([refetchSubscriptions(), refetchHistory()]);
    showToast('Credit Block lists refreshed.');
  };
  const refreshSubscriptionWorkspace = async () => {
    await Promise.allSettled([refetchBureaus(), refetchCases(), refetchSubscriptions()]);
    showToast('Subscription workspace refreshed.');
  };

  const { runToolbarRequestFlow } = useCreditBlockToolbarFlows({
    client,
    formData: { bureauCredentials: formData.bureauCredentials },
    bureauCredentials,
    caseCreating,
    setCaseCreating,
    setToolbarFlowProgress,
    setActiveToolbarSubmit,
    closeToolbarModal: (requestType) => {
      if (requestType === 'lock_request') dispatchToolbarModal({ type: 'close', key: 'block' });
      if (requestType === 'unlock_request') dispatchToolbarModal({ type: 'close', key: 'unblock' });
      if (requestType === 'extension') dispatchToolbarModal({ type: 'close', key: 'extension' });
      if (requestType === 'permanent_unblock') dispatchToolbarModal({ type: 'close', key: 'perm' });
    },
    refetchCases,
    refetchBureaus,
    refetchHistory,
    showToast,
    sendAckEmailMutate: sendAckEmail.mutate,
  });

  const displayFiles = USE_GOBODO_API ? gobodoFiles : files;

  // Build flat record data for DynamicTabRenderer (matches CyberSweep mergeRecordData pattern)
  // DynamicFormSection.getRecordFieldValue() only does flat key lookups, so we must
  // flatten nested CreditBlockClient fields to both camelCase and Gobodo __c keys.
  const recordData = useMemo(() => {
    const contact = client.contact ?? {};
    const personal = client.personalInfo ?? {};
    const mailing = client.addresses?.mailing ?? {};
    const mgmt = client.accountManagement ?? {};
    const sys = client.systemInfo ?? {};
    const arr = client.arrayInfo ?? {};
    const suffix = getCustomSuffix();
    const p0 = client.assignedProducts?.[0];
    const billingConsentDateRaw =
      (client as any).billingConsentDate ??
      (client as any).billingConsentOn ??
      (client as any)[`Billing_Consent_Date${suffix}`] ??
      (client as any).Billing_Consent_Date__c ??
      '';
    const billingConsentDate = typeof billingConsentDateRaw === 'string' ? billingConsentDateRaw : '';

    return {
      // Flat CreditBlockClient fields (already at top level)
      id: client.id,
      accountName: client.accountName,
      accountOwner: client.accountOwner,
      subscriptionStatus: client.subscriptionStatus,

      // Contact fields — camelCase + Gobodo __c variants
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      mobile: contact.mobile ?? '',
      teamEmail: contact.teamEmail ?? '',
      emailCCs: (contact.emailCCs ?? []).join(', '),
      regalTeamEmail: contact.teamEmail ?? '',
      [`PersonEmail${suffix}`]: contact.email ?? '',
      [`Phone${suffix}`]: contact.phone ?? '',
      [`PersonMobilePhone${suffix}`]: contact.mobile ?? '',
      [`Regal_Team_Email${suffix}`]: contact.teamEmail ?? '',

      // Personal info
      dateOfBirth: personal.dateOfBirth ?? '',
      socialSecurityNumber: personal.socialSecurityNumber ?? '',
      [`Birthday${suffix}`]: personal.dateOfBirth ?? '',
      [`SSN${suffix}`]: personal.socialSecurityNumber ?? '',

      // Extended account fields (now on CreditBlockClient interface, mapped in creditBlockQueries)
      spouse: client.spouse ?? '',
      primaryParent: client.primaryParent ?? '',
      numberOfCollectionAccounts: client.numberOfCollectionAccounts ?? '',
      estimatedNetworth: client.estimatedNetworth ?? '',
      annualSalary: client.annualSalary ?? '',
      creditReportFlag: client.creditReportFlag ?? '',
      residentialStatus: client.residentialStatus ?? '',
      residentialAmount: client.residentialAmount ?? '',
      employmentStatus: client.employmentStatus ?? '',
      employer: client.employer ?? '',
      dataSource: client.dataSource ?? '',
      relatedInstitutionalOpportunity: client.relatedInstitutionalOpportunity ?? '',
      bulkSmsStatus: client.bulkSmsStatus ?? '',
      bulkEmailStatus: client.bulkEmailStatus ?? '',
      referralSourceLocation: client.referralSourceLocation ?? '',
      leadSource: client.leadSource ?? '',
      referralCompany: client.referralCompany ?? '',
      referralCompanyIndustry: client.referralCompanyIndustry ?? '',
      referralPartnerContact: client.referralPartnerContact ?? '',
      referralPersonContact: client.referralPersonContact ?? '',
      legacyId: client.legacyId ?? '',

      // Array info
      arrayClientKey: arr.clientKey ?? '',
      arrayRegistrationFailures: (arr.registrationFailures ?? []).join(', '),
      arrayRegistrationMissingData: (client as any).arrayRegistrationMissingData ?? '',

      // Address fields
      billingStreet: mailing.street ?? '',
      billingCity: mailing.city ?? '',
      billingState: mailing.state ?? '',
      billingPostalCode: mailing.zipCode ?? '',
      billingCountry: mailing.country ?? '',
      sequences: (client as any).sequences ?? '',
      [`BillingStreet${suffix}`]: mailing.street ?? '',
      [`BillingCity${suffix}`]: mailing.city ?? '',
      [`BillingState${suffix}`]: mailing.state ?? '',
      [`BillingPostalCode${suffix}`]: mailing.zipCode ?? '',
      billingConsentDate,
      [`Billing_Consent_Date${suffix}`]: billingConsentDate,
      Billing_Consent_Date__c: billingConsentDate,

      // Account management / service
      managingFirm: mgmt.managingFirm ?? '',
      accountManagerCompany: mgmt.accountManagerCompany ?? '',
      accountManager: mgmt.accountManager ?? '',
      dayToDayContact: mgmt.dayToDayContact ?? '',

      // System info
      recordCreatedBy: sys.recordCreatedBy ?? '',
      lastModifiedBy: sys.lastModifiedBy ?? '',
      createdDate: sys.createdDate ?? '',
      lastModifiedDate: sys.lastModifiedDate ?? '',

      // Credit Block subscription (first product / SF CreditBlock__c snapshot)
      [`Credit_Block_Stage${suffix}`]: p0?.subscriptionStage ?? p0?.status ?? '',
      [`Credit_Block_Start_Date${suffix}`]: p0?.enrollmentDate ?? '',
      [`Credit_Program_Type${suffix}`]: p0?.creditProgramType ?? '',
      [`Opt_out_Prescreen_Status${suffix}`]: p0?.optOutPrescreenStatus ?? '',
      [`Priority_Notes${suffix}`]: p0?.priorityNotes ?? '',
      creditProgramType: p0?.creditProgramType ?? '',
      subscriptionStageLabel: p0?.subscriptionStage ?? '',
      openCasesCount: p0?.openCases ?? p0?.activeCasesOpen ?? '',
      creditBlockNotes:
        client.notes?.length > 0
          ? client.notes.map((n) => (n.author && n.author !== '—' ? `${n.author}: ` : '') + n.content).join('\n\n')
          : '',

      // Data for customTabContent renderers (not used by DynamicFormSection)
      bureauCredentials,
      history: historyEntries,
      cases: creditBlockCases,
      files: displayFiles,
      creditReportRecords: clientCRRecords,
    };
  }, [client, bureauCredentials, historyEntries, creditBlockCases, displayFiles, clientCRRecords]);

  const sectionTransformByTabId = useMemo(() => ({
    cb_account_detail: (sections: any[]) => {
      if (!Array.isArray(sections) || sections.length === 0) return sections;
      const renamedSections = sections.map((s) => {
        const isSystemInfoSection =
          s?.id === 'cb_system_info' || s?.name === 'system_information' || s?.label === 'System Information';
        if (s?.id === 'cb_billing_address' || s?.name === 'billing_address' || s?.label === 'Billing Address') {
          return { ...s, label: 'Address Information' };
        }
        if (
          s?.id === 'cb_service_mgmt' ||
          s?.name === 'service_management' ||
          s?.label === 'Sequences / Service Management' ||
          s?.label === 'Account Management Sequences / Service Management'
        ) {
          return {
            ...s,
            label: 'Account Management',
            config: {
              ...(s?.config ?? {}),
              fields: [
                {
                  id: 'cb_sm_service',
                  fieldName: 'managingFirm',
                  fieldLabel: 'Service Management',
                  fieldType: 'select',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 1,
                  options: [
                    { value: '', label: '--None--' },
                    { value: 'Self', label: 'Self' },
                    { value: 'Account Manager Managed', label: 'Account Manager Managed' },
                  ],
                },
                {
                  id: 'cb_sm_manager',
                  fieldName: 'accountManager',
                  fieldLabel: 'Account Manager',
                  fieldType: 'lookup',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 2,
                  options: caseOwnerOptions.map((name) => ({ value: name, label: name })),
                },
                {
                  id: 'cb_sm_managerco',
                  fieldName: 'accountManagerCompany',
                  fieldLabel: 'Account Manager Company',
                  fieldType: 'lookup',
                  lookupObject: 'Account',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 1,
                  order: 3,
                },
                {
                  id: 'cb_sm_daytoday',
                  fieldName: 'dayToDayContact',
                  fieldLabel: 'Day-to-Day Contact',
                  fieldType: 'lookup',
                  required: false,
                  readonly: false,
                  width: 'full',
                  column: 2,
                  order: 4,
                  options: caseOwnerOptions.map((name) => ({ value: name, label: name })),
                },
              ],
            },
          };
        }
        if (isSystemInfoSection) {
          const fields = Array.isArray(s?.config?.fields) ? s.config.fields : [];
          return {
            ...s,
            config: {
              ...(s?.config ?? {}),
              fields: fields.map((f: any) => ({ ...f, readonly: true })),
            },
          };
        }
        if (Array.isArray(s?.config?.fields)) {
          return {
            ...s,
            config: {
              ...(s?.config ?? {}),
              fields: s.config.fields.map((f: any) => ({ ...f, readonly: f.readonly ?? false })),
            },
          };
        }
        return s;
      });
      return renamedSections.filter((s) => s?.id !== 'cb_billing_consent' && s?.name !== 'billing_consent');
    },
  }), [caseOwnerOptions]);

  // Get display name
  const displayName = client.accountName || 'Unknown Client';
  const activityRecordId = String(
    (client as any).key ??
      client.arrayInfo?.clientKey ??
      client.id ??
      recordData?.arrayClientKey ??
      (client as any).sf_id ??
      (client as any).salesforce_id ??
      (recordData as any)?.sf_id ??
      ''
  ).trim();
  // Array client key is a UUID provisioned at onboarding (mapped from Gobodo's
  // ArrayClientKey__c → client.arrayInfo.clientKey at creditBlockQueries.ts:323).
  // SF Account IDs and Gobodo object keys are NOT valid substitutes; sending
  // one to Array's /order-report would 400 (or silently misroute). When this is
  // empty, the panel render is gated off below at the call site.
  const arrayClientKey = (
    client.arrayInfo?.clientKey ??
    recordData?.arrayClientKey ??
    ''
  ).trim();
  const createClientActivityFromAction = async (
    type: 'task' | 'meeting' | 'call' | 'note' | 'email',
    opts?: { subject?: string; description?: string; scheduledAt?: string }
  ) => {
    if (!activityRecordId) {
      showToast('Missing client id for activity', 'error');
      return;
    }
    const label =
      type === 'meeting'
        ? 'Event'
        : type === 'note'
          ? 'Post'
          : type.charAt(0).toUpperCase() + type.slice(1);

    try {
      await createClientActivity({
        type,
        recordId: activityRecordId,
        recordType: 'client',
        userId: userId || 'unknown',
        userName: userName || 'Unknown User',
        subject: (opts?.subject || `${label} · ${displayName}`).trim(),
        description: (opts?.description || '').trim() || undefined,
        ...(opts?.scheduledAt ? { scheduledAt: opts.scheduledAt } : {}),
        source: 'manual',
        sourceApp: 'credit_block',
      });
      setClientActivitySavedAt(Date.now());
      showToast(`${label} activity created`);
    } catch (error: any) {
      showToast(error?.message || `Failed to create ${label.toLowerCase()} activity`, 'error');
    }
  };

  const openClientActivityComposer = (type: 'task' | 'meeting' | 'call' | 'note' | 'email') => {
    if (type === 'task') {
      setShowNewTaskModal(true);
      return;
    }
    setClientActivityModalType(type);
    setShowClientActivityModal(true);
  };

  // Helpers
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
      case 'cancelled': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
      case 'suspended': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300';
      case 'pending': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300';
      case 'completed': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const toggleSection = (sectionId: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionId)) {
      newCollapsed.delete(sectionId);
    } else {
      newCollapsed.add(sectionId);
    }
    setCollapsedSections(newCollapsed);
  };

  const togglePasswordVisibility = (bureau: string) => {
    setShowPasswords((prev) => ({ ...prev, [bureau]: !prev[bureau] }));
  };

  // File handlers (kept for custom file tab component)
  const handleFileDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleFileDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const dropped = e.dataTransfer.files;
    if (dropped?.length) await handleFilesAdded(Array.from(dropped));
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    const input = e.target;
    if (selected?.length) await handleFilesAdded(Array.from(selected));
    input.value = '';
  };

  const handleDownloadFile = async (file: CreditBlockFile) => {
    if (!USE_GOBODO_API) return;
    if (file.downloadUrl) {
      window.open(file.downloadUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const fileId = Number(file.id);
    if (Number.isNaN(fileId)) return;
    try {
      const meta = await getFileForDownload(fileId);
      if (meta.downloadUrl) {
        window.open(meta.downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (_err) {
      // Error logged in service
    }
  };

  const handleFilesAdded = async (fileList: File[]) => {
    if (!canEditCreditBlock) return;
    if (USE_GOBODO_API) {
      const reltypekey = getRelTypeKeyForUpload(fileCategoryFilter as CreditBlockFileCategory);
      for (const file of fileList) {
        try {
          await uploadFileMutation.mutateAsync({ file, reltypekey });
        } catch (_err) {
          // toast is handled by the uploadFileMutation.isError effect
        }
      }
      return;
    }
    const newFiles: CreditBlockFile[] = fileList.map((f) => ({
      id: `file-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      fileName: f.name,
      fileType: f.type || 'application/octet-stream',
      fileSize: f.size,
      uploadedBy: client.accountOwner || 'Unknown',
      uploadedDate: new Date().toISOString(),
      category: 'other',
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    showToast(`${newFiles.length} file${newFiles.length > 1 ? 's' : ''} uploaded successfully`);
  };

  const goToCreditReportTab = () => {
    setLocalCreditPullOpen(true);
  };

  const navigateToSubscriptionWorkspace = (row: CreditBlockProduct) => {
    setSelectedSubscriptionId(row.id);
    const cid = encodeURIComponent(client.id);
    navigate(`/credit-block/client/${cid}/subscription?subscriptionId=${encodeURIComponent(row.id)}`, { replace: true });
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('openMainTab') !== 'subscription') return;
    const subscriptionId = params.get('subscriptionId')?.trim();
    const cid = encodeURIComponent(client.id);
    const q = subscriptionId ? `?subscriptionId=${encodeURIComponent(subscriptionId)}` : '';
    navigate(`/credit-block/client/${cid}/subscription${q}`, { replace: true });
  }, [client.id, navigate]);

  const handleBureauFieldSave = (bureauKey: string, fieldKey: string, value: string | boolean) => {
    const boolKeys = ['pinMailed', 'blockProcessCollection', 'unblockProcessCollection'];
    const v = boolKeys.includes(fieldKey) ? value === true || value === 'true' : value;
    const updates: Record<string, unknown> = { [fieldKey]: v };
    if (fieldKey === 'startDate' && typeof v === 'string') updates.enrollmentDate = v;
    if (fieldKey === 'endDate' && typeof v === 'string') updates.unblockDate = v;
    if (fieldKey === 'status') {
      const normalized = String(v).trim().toLowerCase();
      if (normalized === 'locked' || normalized === 'blocked') {
        updates.startDate = '';
        updates.enrollmentDate = '';
        updates.endDate = '';
        updates.unblockDate = '';
      }
    }
    const cred = formData.bureauCredentials[bureauKey] ?? {};
    setFormData({
      ...formData,
      bureauCredentials: {
        ...formData.bureauCredentials,
        [bureauKey]: { ...cred, ...updates },
      },
    });
    if (USE_GOBODO_API && cred.creditBureauId) {
      updateBureau.mutate(
        { bureau: bureauKey, creditBureauId: cred.creditBureauId, fieldKey, value: v },
        {
          onSuccess: () => showToast(`${BUREAU_LABELS[bureauKey] || bureauKey} ${fieldKey} saved`),
          onError: () => showToast(`Failed to save ${fieldKey}`, 'error'),
        }
      );
    }
  };

  const submitFirstTimeBlock = async () => {
    if (caseCreating || !canEditCreditBlock) return;
    setCaseCreating(true);
    setToolbarFlowProgress({
      flowTitle: 'First Time Block',
      phase: USE_GOBODO_API ? 'Resolving Credit Block record type…' : 'Preparing case…',
    });
    const ownerName = '';
    const resolvedOwnerUserId = '';
    const startDate = new Date().toISOString().slice(0, 10);
    try {
      const casePayload = buildCreditBlockCaseCreatePayload({
        clientId: client.id,
        clientName: client.accountName,
        owner: ownerName,
        ownerUserId: resolvedOwnerUserId,
        subject: `${client.accountName} · First Time Block`,
        subType: 'Lock / Block',
        priority: 'High',
        origin: CREDIT_BLOCK_SYSTEM_CASE_ORIGIN,
        description: `Created from Credit Bureaus toolbar. Flow: First Time Block. Owner: ${ownerName}.`,
      });
      if (USE_GOBODO_API) {
        const recordTypeTarget = await findCreditBlockRecordTypeTarget();
        applyCreditBlockRecordTypeFields(casePayload, recordTypeTarget);
      }
      setToolbarFlowProgress({ flowTitle: 'First Time Block', phase: 'Creating case…' });
      const caseRec = await createRecord('Case__c', casePayload as Record<string, unknown>);
      const caseId = graphKeyFromCreatedRecord(caseRec as { id?: unknown; key?: unknown });

      setToolbarFlowProgress({
        flowTitle: 'First Time Block',
        phase: 'Filing bureau requests (Equifax, Experian, TransUnion)…',
        step: 1,
        total: 1,
      });
      await createUnlockReq.mutateAsync({
        bureau: 'all_three',
        requestType: 'lock_request',
        reason: `Source: First Time Block · Owner: ${ownerName}`,
        startDate,
        caseId: caseId || undefined,
      });

      // Update bureau Stage__c → Locked for all three bureaus
      const stageUpdates = (['equifax', 'experian', 'transunion'] as const)
        .map((b) => ({
          bureau: b,
          creditBureauId: formData.bureauCredentials[b]?.creditBureauId || '',
          stage: 'Locked' as const,
        }))
        .filter((u) => u.creditBureauId);
      if (stageUpdates.length > 0) {
        setToolbarFlowProgress({
          flowTitle: 'First Time Block',
          phase: 'Updating bureau stages to Locked…',
          step: 1,
          total: 1,
        });
        try {
          await updateBureauStages.mutateAsync(stageUpdates);
        } catch {
          // Stage update is best-effort; case + requests already created
        }
      }

      // Send auto-acknowledgment email (non-blocking)
      const clientEmail = client.contact?.email?.trim();
      if (clientEmail) {
        setToolbarFlowProgress({
          flowTitle: 'First Time Block',
          phase: 'Queueing confirmation email…',
          step: 1,
          total: 1,
        });
        sendAckEmail.mutate({
          to: [clientEmail],
          subject: `Request Received — ${client.accountName} · First Time Block`,
          body: [
            `Dear ${client.accountName || 'Client'},`,
            '',
            'Your First Time Block request has been received and a case has been created.',
            '',
            `Case ID: ${caseId || 'Pending'}`,
            'Request Type: First Time Block',
            'Bureaus: Equifax, Experian, TransUnion',
            '',
            'Our team will process your request and you will receive a confirmation once completed.',
            '',
            'Thank you,',
            'Regal Credit Block Operations',
          ].join('\n'),
        });
      }

      showToast(
        caseId
          ? `First Time Block: case ${caseId} created; Credit Block history filed (Block) for Equifax, Experian, TransUnion.`
          : 'First Time Block: bureau requests filed; case id not returned — check Cases list.'
      );
      dispatchToolbarModal({ type: 'close', key: 'firstTime' });
      if (caseId) {
        const initialCaseStatus = ownerName ? 'Assigned' : 'New';
        setSelectedCaseRecord({
          id: caseId,
          caseNumber: caseId,
          subject: `${client.accountName} · First Time Block`,
          status: initialCaseStatus,
          owner: ownerName,
          accountName: client.accountName,
          accountId: client.id,
        } as ListViewRecord);
      }
    } catch (err) {
      showToast(
        `First Time Block failed: ${err instanceof Error ? err.message : 'could not create case or file bureau requests.'}`,
        'error'
      );
    } finally {
      setToolbarFlowProgress(null);
      setCaseCreating(false);
    }
  };

  const submitPermanentUnblock = async () => {
    const picked = BUREAUS.filter((b) => permSelectedBureaus[b]);
    if (picked.length === 0) {
      showToast('Select at least one bureau', 'error');
      return;
    }
    if (!permRequestSource.trim()) {
      showToast('Select a request source', 'error');
      return;
    }
    await runToolbarRequestFlow({
      requestType: 'permanent_unblock',
      bureaus: picked as Array<'equifax' | 'experian' | 'transunion' | 'chexsystems'>,
      source: permRequestSource,
      startDate: new Date().toISOString().slice(0, 10),
      failureMessage: 'Failed to create Permanent Unblock request',
    });
  };

  const submitUnblockBureaus = async () => {
    const eligible = BUREAUS.filter((b) => !isUnblockedBureau(bureauCredentials[b]));
    const picked = eligible.filter((b) => unblockSelectedBureaus[b]);
    if (picked.length === 0) {
      showToast('Select at least one bureau to unblock', 'error');
      return;
    }
    if (!unblockStartDate) {
      showToast('Unblock start date is required', 'error');
      return;
    }
    if (unblockEndLength === 'custom' && !unblockEndDate) {
      showToast('Custom end date is required when end date length is Custom', 'error');
      return;
    }
    const resolvedUnblockEndDate = resolveEndDateFromLength(
      unblockStartDate,
      unblockEndLength || undefined,
      unblockEndDate || undefined
    );
    await runToolbarRequestFlow({
      requestType: 'unlock_request',
      bureaus: picked as Array<'equifax' | 'experian' | 'transunion' | 'chexsystems'>,
      source: unblockRequestSource || 'None',
      startDate: unblockStartDate,
      endDate: resolvedUnblockEndDate,
      endLength: unblockEndLength || undefined,
      failureMessage: 'Failed to create unblock request',
    });
  };

  const submitExtensionBureaus = async () => {
    const eligible = BUREAUS.filter((b) => isUnblockedBureau(bureauCredentials[b]));
    const picked = eligible.filter((b) => extensionSelectedBureaus[b]);
    if (picked.length === 0) {
      showToast('Select at least one bureau for extension', 'error');
      return;
    }
    if (!extensionEndDate && !extensionEndLength) {
      showToast('Select a new end date or extension length', 'error');
      return;
    }
    const latestCurrentEnd = picked.reduce((latest, b) => {
      const cred = bureauCredentials[b];
      const end = cred?.endDate || cred?.unblockDate || '';
      return end > latest ? end : latest;
    }, '');
    const anchorEndForLength = latestCurrentEnd || new Date().toISOString().slice(0, 10);
    const earliestUnblockStart = picked.reduce((earliest: string, b) => {
      const cred = bureauCredentials[b];
      const s = String(cred?.startDate || cred?.enrollmentDate || '').trim();
      if (!s) return earliest;
      if (!earliest) return s;
      return s < earliest ? s : earliest;
    }, '');
    const extensionRecordStart =
      earliestUnblockStart ||
      String(extensionStartDate || '').trim() ||
      new Date().toISOString().slice(0, 10);
    const resolvedExtensionEndDate = extensionEndDate
      ? extensionEndDate
      : resolveEndDateFromLength(anchorEndForLength, extensionEndLength || undefined, undefined);
    await runToolbarRequestFlow({
      requestType: 'extension',
      bureaus: picked as Array<'equifax' | 'experian' | 'transunion' | 'chexsystems'>,
      source: extensionRequestSource || 'None',
      startDate: extensionRecordStart,
      endDate: resolvedExtensionEndDate,
      endLength: extensionEndLength || undefined,
      failureMessage: 'Failed to create extension request',
    });
  };

  const submitBlockBureaus = async () => {
    const eligible = BUREAUS.filter((b) => !isLockedOrBlockedBureau(bureauCredentials[b]));
    const picked = eligible.filter((b) => blockSelectedBureaus[b]);
    if (picked.length === 0) {
      showToast('Select at least one bureau to block', 'error');
      return;
    }
    await runToolbarRequestFlow({
      requestType: 'lock_request',
      bureaus: picked as Array<'equifax' | 'experian' | 'transunion' | 'chexsystems'>,
      source: blockRequestSource || 'None',
      failureMessage: 'Failed to create block request',
    });
  };

  const handleSave = () => {
    if (USE_GOBODO_API) {
      const bureauUpdates = BUREAUS
        .map((bureau) => {
          const cred = formData.bureauCredentials[bureau] ?? {};
          const original = bureauCredentials[bureau] ?? {};
          const changes: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(cred)) {
            if (k !== 'creditBureauId' && v !== (original as Record<string, unknown>)[k]) {
              changes[k] = v;
            }
          }
          return { bureau, creditBureauId: cred.creditBureauId ?? '', changes };
        })
        .filter((u) => u.creditBureauId && Object.keys(u.changes).length > 0);
      if (bureauUpdates.length > 0) {
        saveBureauCredentials.mutate(bureauUpdates, {
          onSuccess: () => showToast(`Saved ${bureauUpdates.length} bureau(s) to Gobodo`),
          onError: () => showToast('Failed to save bureau credentials', 'error'),
        });
      } else {
        showToast('No changes to save');
      }
    }
  };

  // Client edit modal handlers
  const openClientEditModal = () => {
    setClientEditValues({
      accountName: client.accountName ?? '',
      phone: client.contact?.phone ?? '',
      mobile: client.contact?.mobile ?? '',
      email: client.contact?.email ?? '',
      spouse: (client as any).spouse ?? '',
      employer: (client as any).employer ?? '',
      employmentStatus: (client as any).employmentStatus ?? '',
      residentialStatus: (client as any).residentialStatus ?? '',
      billingStreet: client.addresses?.mailing?.street ?? '',
      billingCity: client.addresses?.mailing?.city ?? '',
      billingState: client.addresses?.mailing?.state ?? '',
      billingPostalCode: client.addresses?.mailing?.zipCode ?? '',
    });
    setClientEditModalOpen(true);
  };
  const saveClientEditModal = async () => {
    if (!USE_GOBODO_API) return;
    setClientEditSaving(true);
    try {
      const suffix = getCustomSuffix() || '__c';
      const updates: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(clientEditValues)) {
        updates[key] = value || null;
      }
      await updateRecord(`Client${suffix}`, client.id, updates);
      showToast('Client updated');
      setClientEditModalOpen(false);
    } catch {
      showToast('Failed to update client', 'error');
    } finally {
      setClientEditSaving(false);
    }
  };

  const renderCreditBlockOverviewTab = () => {
    if (selectedHistoryEntry) {
      return (
        <HistoryDetailView
          entry={selectedHistoryEntry}
          clientName={client.accountName}
          onBack={() => setSelectedHistoryEntry(null)}
        />
      );
    }
    return (
      <CreditBlockOverviewTabContent
        products={subscriptionRows}
        subscriptionsLoading={subscriptionsLoading}
        historyEntries={historyEntries}
        historyLoading={historyLoading}
        historyError={historyError}
        onSettings={handleCreditBlockSettingsClick}
        onRefresh={() => { void refreshCreditBlockOverview(); }}
        onSubscriptionClick={navigateToSubscriptionWorkspace}
        onHistoryClick={setSelectedHistoryEntry}
      />
    );
  };

  const renderCreditBlockSubscriptionWorkspaceTab = () => {
    // Bureau detail drill-in — use formData (optimistically updated) so saves are immediately visible
    if (selectedBureauKey) {
      const cred = formData.bureauCredentials[selectedBureauKey] ?? bureauCredentials[selectedBureauKey] ?? {};
      return (
        <BureauDetailView
          bureauKey={selectedBureauKey}
          bureauLabel={BUREAU_LABELS[selectedBureauKey] || selectedBureauKey}
          credential={cred}
          clientName={client.accountName}
          accountOwner={client.accountOwner}
          onBack={() => setSelectedBureauKey(null)}
          onSaveField={handleBureauFieldSave}
        />
      );
    }
    return (
      <CreditBlockSubscriptionWorkspaceTabContent
        accountName={client.accountName}
        subscriptionRows={subscriptionRows}
        subscriptionsLoading={subscriptionsLoading}
        selectedSubscriptionId={selectedSubscriptionId}
        onSelectSubscriptionId={setSelectedSubscriptionId}
        bureauCredentials={bureauCredentials}
        bureausLoading={bureausLoading}
        creditBlockCases={creditBlockCases}
        casesLoading={casesLoading}
        onBureauAction={(kind) => {
          if (kind === 'first_time_block') {
            dispatchToolbarModal({ type: 'open', key: 'firstTime' });
            return;
          }
          if (kind === 'block_bureaus') {
            dispatchToolbarModal({ type: 'open', key: 'block' });
            return;
          }
          if (kind === 'unlock_request') {
            dispatchToolbarModal({ type: 'open', key: 'unblock' });
            return;
          }
          if (kind === 'extension') {
            dispatchToolbarModal({ type: 'open', key: 'extension' });
            return;
          }
          if (kind === 'permanent_unblock') {
            dispatchToolbarModal({ type: 'open', key: 'perm' });
            return;
          }
          setNewRequestForm((prev) => ({ ...prev, requestType: kind, startDate: '', endDate: '' }));
          setShowNewRequestModal(true);
        }}
        onSettings={handleCreditBlockSettingsClick}
        onRefresh={() => { void refreshSubscriptionWorkspace(); }}
        onBureauClick={setSelectedBureauKey}
        onCaseClick={setSelectedCaseRecord}
        onUpdateSubscriptionField={async (subscription, updates) => {
          await updateSubscriptionField.mutateAsync({
            clientId: client.id,
            subscription,
            updates,
          });
          showToast('Subscription updated');
        }}
        isUpdatingSubscription={updateSubscriptionField.isPending}
        canEditCreditBlock={canEditCreditBlock}
        canStartFirstTimeBlock={canEditCreditBlock}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Clients
          </button>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">{displayName}</h1>
              {client.accountOwner && !/^[0-9a-zA-Z]{15,18}$/.test(client.accountOwner) && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Account Owner: {client.accountOwner}</p>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={openClientEditModal}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Edit3 className="h-4 w-4" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  // When Array credit pull is enabled, route to the Credit Report
                  // tab where ArrayCreditPullPanel handles the automated flow.
                  // Fall back to the manual entry form only when the feature flag
                  // is off (e.g. production until backend wiring is verified).
                  if (isArrayCreditPullEnabled() && arrayClientKey) {
                    setActiveTab('credit-report');
                  } else {
                    setLocalCreditPullOpen(true);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-all"
              >
                <FileText className="h-4 w-4" />
                Pull Credit Report
              </button>
              <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(client.subscriptionStatus)}`}>
                {client.subscriptionStatus.charAt(0).toUpperCase() + client.subscriptionStatus.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Case detail drill-in — renders above tabs from any context */}
        {selectedCaseRecord ? (
          <CaseDetailView
            caseRecord={selectedCaseRecord}
            clientName={client.accountName}
            onBack={() => setSelectedCaseRecord(null)}
            onOpenClientDetails={onOpenClientDetails}
          />
        ) : (
        <DynamicTabRenderer
          applicationName="credit_block"
          pageId="credit_block_client_detail"
          recordId={client.id}
          recordData={recordData}
          recordType="client"
          userRole="admin"
          hiddenTabIds={['bureaus']}
          onFieldSave={USE_GOBODO_API ? async (fieldName, value) => {
            const suffix = getCustomSuffix() || '__c';
            const clientObjectType = `Client${suffix}`;
            const { gobodoFieldName, coercedValue } = buildCreditBlockLookupSavePayload(fieldName, value, suffix);
            return await updateRecord(clientObjectType, client.id, { [gobodoFieldName]: coercedValue });
          } : undefined}
          contactName={displayName}
          phoneNumber={client.contact?.phone || client.contact?.mobile || ''}
          relatedToName={client.accountName || displayName}
          onTabChange={(tabId) => {
            setActiveTab(tabId);
            setTabNavigationRequest(null);
            setSelectedBureauKey(null); // reset drill-in views when changing tabs
            setSelectedCaseRecord(null);
            setSelectedHistoryEntry(null);
            syncTabSlugInUrl(tabId);
          }}
          tabNavigationRequest={tabNavigationRequest}
          hideSectionDescription
          sectionTransformByTabId={sectionTransformByTabId}
          // Custom tab content renderers for specialized tabs
          customTabContent={{
            // Tab 1 (cb_account_detail) uses DynamicTabRenderer default — no custom entry needed
            // Tab 2: Credit Block — Subscriptions + Histories related lists only
            cb_client_details_tab: renderCreditBlockOverviewTab,
            details: renderCreditBlockOverviewTab,
            // Tab 3: Subscription workspace
            cb_subscription_workspace: renderCreditBlockSubscriptionWorkspaceTab,
            // Tab 4: Activity — SF-style timeline
            activity: () => (
              <SalesforceActivityTab
                key={`client-activity-${activityRecordId}-${clientActivitySavedAt}`}
                recordId={activityRecordId}
                recordType="client"
                contactName={displayName}
                activities={creditBlockActivities}
                loading={creditBlockCaseLookups.length > 0 && (casesLoading || creditBlockActivitiesLoading)}
                disableQueryFetch
                onLogActivity={(type) => {
                  openClientActivityComposer(type);
                }}
              />
            ),
            notes: () => (
              <CreditBlockNotesWorkspaceTabContent
                client={client}
                files={displayFiles}
                historyEntries={historyEntries}
                subscriptions={subscriptionRows}
                cases={creditBlockCases}
                onSubscriptionClick={navigateToSubscriptionWorkspace}
                onCaseClick={setSelectedCaseRecord}
                renderNotesPanel={() => (
                  <ClientNotesPanel
                    clientId={client.id}
                    initialText={String(recordData.creditBlockNotes ?? '')}
                    showToast={showToast}
                  />
                )}
              />
            ),
            // Bureaus tab — drill-in to bureau detail or show accordion list
            bureaus: () => {
              if (selectedBureauKey) {
                const cred = formData.bureauCredentials[selectedBureauKey] ?? bureauCredentials[selectedBureauKey] ?? {};
                return (
                  <BureauDetailView
                    bureauKey={selectedBureauKey}
                    bureauLabel={BUREAU_LABELS[selectedBureauKey] || selectedBureauKey}
                    credential={cred}
                    clientName={client.accountName}
                    accountOwner={client.accountOwner}
                    onBack={() => setSelectedBureauKey(null)}
                    onSaveField={handleBureauFieldSave}
                  />
                );
              }
              return (
                <BureausTabContent
                  bureauCredentials={bureauCredentials}
                  formData={formData}
                  setFormData={setFormData}
                  collapsedSections={collapsedSections}
                  toggleSection={toggleSection}
                  editingField={editingField}
                  setEditingField={setEditingField}
                  tempEditValue={tempEditValue}
                  setTempEditValue={setTempEditValue}
                  showPasswords={showPasswords}
                  togglePasswordVisibility={togglePasswordVisibility}
                  handleSave={handleSave}
                  showToast={showToast}
                  updateBureau={updateBureau}
                  bureausLoading={bureausLoading}
                  bureausError={bureausError}
                  subscriptionCount={subscriptionRows.length}
                  onBureauClick={setSelectedBureauKey}
                />
              );
            },
            // Files tab
            files: () => (
              <FilesTabContent
                displayFiles={displayFiles}
                filesLoading={filesLoading}
                filesError={filesError}
                fileCategoryFilter={fileCategoryFilter}
                setFileCategoryFilter={setFileCategoryFilter}
                fileCounts={fileCounts}
                dragActive={dragActive}
                handleFileDrag={handleFileDrag}
                handleFileDrop={handleFileDrop}
                handleFileInputChange={handleFileInputChange}
                handleFilesAdded={handleFilesAdded}
                handleDownloadFile={handleDownloadFile}
                showToast={showToast}
                fileInputRef={fileInputRef}
                isUploading={uploadFileMutation.isPending}
                clientCRRecords={clientCRRecords}
                setSelectedCRRecord={setSelectedCRRecord}
                sfFiles={sfFiles}
                sfFilesLoading={sfFilesLoading}
              />
            ),
            // Credit Report tab
            'credit-report': () => {
              if (selectedCRRecord) {
                return (
                  <CreditReportDetailView
                    record={selectedCRRecord}
                    clientName={client.accountName}
                    onBack={() => setSelectedCRRecord(null)}
                  />
                );
              }
              return (
                <div className="space-y-4">
                  {isArrayCreditPullEnabled() && arrayClientKey && (
                    <ArrayCreditPullPanel
                      arrayClientKey={arrayClientKey}
                      clientName={displayName}
                      clientId={String(client.id)}
                      ssnLast4={String(client.personalInfo?.socialSecurityNumber ?? '').replace(/\D/g, '').slice(-4) || undefined}
                    />
                  )}
                  <CreditReportTabContent
                    clientCRRecords={clientCRRecords}
                    crRecordsLoading={crRecordsLoading}
                    crRecordsError={crRecordsError}
                    setSelectedCRRecord={setSelectedCRRecord}
                    onNewCreditReport={goToCreditReportTab}
                  />
                </div>
              );
            },
          }}
        />
        )}
      </div>

      {/* Client edit modal */}
      {clientEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={() => !clientEditSaving && setClientEditModalOpen(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Edit Client</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{displayName}</p>
            </div>
            <div className={`px-6 py-5 space-y-4 max-h-[60vh] overflow-y-auto ${clientEditSaving ? 'opacity-50 pointer-events-none' : ''}`}>
              {[
                { key: 'accountName', label: 'Account Name', type: 'text' },
                { key: 'phone', label: 'Phone', type: 'tel' },
                { key: 'mobile', label: 'Mobile', type: 'tel' },
                { key: 'email', label: 'Email', type: 'email' },
                { key: 'spouse', label: 'Spouse', type: 'text' },
                { key: 'employer', label: 'Employer', type: 'text' },
                { key: 'employmentStatus', label: 'Employment Status', type: 'text' },
                { key: 'residentialStatus', label: 'Residential Status', type: 'text' },
              ].map(({ key, label, type }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
                  <input
                    type={type}
                    value={clientEditValues[key] ?? ''}
                    onChange={(e) => setClientEditValues((p) => ({ ...p, [key]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                </div>
              ))}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-3">Address</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'billingStreet', label: 'Street' },
                    { key: 'billingCity', label: 'City' },
                    { key: 'billingState', label: 'State' },
                    { key: 'billingPostalCode', label: 'Zip Code' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
                      <input
                        type="text"
                        value={clientEditValues[key] ?? ''}
                        onChange={(e) => setClientEditValues((p) => ({ ...p, [key]: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button type="button" onClick={() => setClientEditModalOpen(false)} disabled={clientEditSaving} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button type="button" onClick={saveClientEditModal} disabled={clientEditSaving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
                {clientEditSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className="fixed top-6 right-6 z-[200] animate-[slideUp_0.2s_ease-out]">
          <div className={`flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'
          }`}>
            {toast.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {toast.message}
            <button onClick={() => setToast(null)} className="ml-2 hover:opacity-70">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {toolbarFlowProgress && caseCreating ? (
        <div
          className="fixed bottom-28 left-1/2 z-[60] w-[min(100%,22rem)] -translate-x-1/2 px-3 pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <div className="rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white">
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {toolbarFlowProgress.flowTitle}
                </p>
                <p className="mt-1 text-sm font-medium leading-snug text-gray-900 dark:text-gray-100">
                  {toolbarFlowProgress.phase}
                </p>
                {toolbarFlowProgress.bureau ? (
                  <p className="mt-1 truncate text-xs text-gray-600 dark:text-gray-300">
                    Bureau:{' '}
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{toolbarFlowProgress.bureau}</span>
                  </p>
                ) : null}
                {toolbarFlowProgress.total != null && toolbarFlowProgress.total > 0 ? (
                  <p className="mt-2 text-[11px] font-medium tabular-nums text-gray-500 dark:text-gray-400">
                    Step {toolbarFlowProgress.step ?? 0} of {toolbarFlowProgress.total}
                  </p>
                ) : null}
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-[width] duration-500 ease-out"
                    style={{
                      width: `${(() => {
                        const t = toolbarFlowProgress.total ?? 0;
                        const s = toolbarFlowProgress.step ?? 0;
                        if (t <= 0) return 40;
                        return Math.min(100, Math.max(10, Math.round((s / t) * 100)));
                      })()}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* New Lock/Unlock Request Modal */}
      {showNewRequestModal && (
        <NewRequestModal
          showNewRequestModal={showNewRequestModal}
          setShowNewRequestModal={setShowNewRequestModal}
          newRequestForm={newRequestForm}
          setNewRequestForm={setNewRequestForm}
          createUnlockReq={createUnlockReq}
          showToast={showToast}
        />
      )}
      {toolbarModals.firstTime && (
        <FirstTimeBlockOwnerModal
          open={toolbarModals.firstTime}
          ownerOptions={workflowCaseOwnerOptions}
          selectedOwner={selectedCaseOwner}
          onSelectOwner={setSelectedCaseOwner}
          onClose={() => dispatchToolbarModal({ type: 'close', key: 'firstTime' })}
          onSubmit={submitFirstTimeBlock}
          submitting={caseCreating}
        />
      )}
      {toolbarModals.perm && (
        <PermanentUnblockOwnerModal
          open={toolbarModals.perm}
          ownerOptions={workflowCaseOwnerOptions}
          selectedOwner={selectedCaseOwner}
          onSelectOwner={setSelectedCaseOwner}
          requestSource={permRequestSource}
          onRequestSourceChange={setPermRequestSource}
          selectedBureaus={permSelectedBureaus}
          onToggleBureau={(bureau) =>
            setPermSelectedBureaus((prev) => ({ ...prev, [bureau]: !prev[bureau] }))
          }
          onClose={() => dispatchToolbarModal({ type: 'close', key: 'perm' })}
          onSubmit={submitPermanentUnblock}
          submitting={caseCreating && activeToolbarSubmit === 'permanent_unblock'}
        />
      )}
      {toolbarModals.unblock && (
        <UnblockBureausModal
          open={toolbarModals.unblock}
          requestSource={unblockRequestSource}
          onRequestSourceChange={setUnblockRequestSource}
          startDate={unblockStartDate}
          onStartDateChange={setUnblockStartDate}
          endDate={unblockEndDate}
          onEndDateChange={setUnblockEndDate}
          endDateLength={unblockEndLength}
          onEndDateLengthChange={(value) => {
            setUnblockEndLength(value);
            if (value !== 'custom') setUnblockEndDate('');
          }}
          selectedBureaus={unblockSelectedBureaus}
          bureauCredentials={bureauCredentials}
          onToggleBureau={(bureau) =>
            setUnblockSelectedBureaus((prev) => ({ ...prev, [bureau]: !prev[bureau] }))
          }
          onClose={() => dispatchToolbarModal({ type: 'close', key: 'unblock' })}
          onSubmit={submitUnblockBureaus}
          submitting={caseCreating && activeToolbarSubmit === 'unlock_request'}
        />
      )}
      {toolbarModals.extension && (
        <ExtensionBureausModal
          open={toolbarModals.extension}
          requestSource={extensionRequestSource}
          onRequestSourceChange={setExtensionRequestSource}
          startDate={extensionStartDate}
          onStartDateChange={setExtensionStartDate}
          endDate={extensionEndDate}
          onEndDateChange={setExtensionEndDate}
          endDateLength={extensionEndLength}
          onEndDateLengthChange={setExtensionEndLength}
          selectedBureaus={extensionSelectedBureaus}
          bureauCredentials={bureauCredentials}
          onToggleBureau={(bureau) =>
            setExtensionSelectedBureaus((prev) => ({ ...prev, [bureau]: !prev[bureau] }))
          }
          onClose={() => dispatchToolbarModal({ type: 'close', key: 'extension' })}
          onSubmit={submitExtensionBureaus}
          submitting={caseCreating && activeToolbarSubmit === 'extension'}
        />
      )}
      {toolbarModals.block && (
        <BlockBureausModal
          open={toolbarModals.block}
          requestSource={blockRequestSource}
          onRequestSourceChange={setBlockRequestSource}
          selectedBureaus={blockSelectedBureaus}
          bureauCredentials={bureauCredentials}
          onToggleBureau={(bureau) =>
            setBlockSelectedBureaus((prev) => ({ ...prev, [bureau]: !prev[bureau] }))
          }
          onClose={() => dispatchToolbarModal({ type: 'close', key: 'block' })}
          onSubmit={submitBlockBureaus}
          submitting={caseCreating && activeToolbarSubmit === 'lock_request'}
        />
      )}

      {/* New Task Modal */}
      {showNewTaskModal && (
        <NewTaskModal
          contactName={client.accountName}
          assignedTo={client.accountOwner}
          onClose={() => setShowNewTaskModal(false)}
          onSave={(data) => {
            const descriptionBits = [
              data.salesActivity ? `Sales Activity: ${data.salesActivity}` : '',
              data.contactName ? `Contact: ${data.contactName}` : '',
              data.assignedTo ? `Assigned To: ${data.assignedTo}` : '',
            ].filter(Boolean);
            void createClientActivityFromAction('task', {
              subject: data.subject,
              description: descriptionBits.join('\n'),
              scheduledAt: data.dueDate ? `${data.dueDate}T00:00:00` : undefined,
            });
            setShowNewTaskModal(false);
          }}
        />
      )}
      {showClientActivityModal && (
        <ClientActivityComposerModal
          mode={clientActivityModalType}
          clientName={displayName}
          onClose={() => setShowClientActivityModal(false)}
          onSave={(payload) => {
            void createClientActivityFromAction(payload.type, {
              subject: payload.subject,
              description: payload.description,
              scheduledAt: payload.scheduledAt,
            });
            setShowClientActivityModal(false);
          }}
        />
      )}

      {localCreditPullOpen && (
        <CreditReportOrderWidget
          isOpen
          onClose={() => setLocalCreditPullOpen(false)}
          clientId={String(client.id ?? client.arrayInfo?.clientKey ?? '')}
          clientName={client.accountName || displayName}
          orderedBy={userName}
          orderedFrom="credit_block"
          onSubmit={async (order: CreditReportOrder) => {
            await creditReportService.orderCreditReport(order);
            showToast('Credit report ordered');
            setLocalCreditPullOpen(false);
          }}
        />
      )}

    </div>
  );
};

const ClientActivityComposerModal: React.FC<{
  mode: 'meeting' | 'call' | 'note' | 'email';
  clientName: string;
  onClose: () => void;
  onSave: (payload: {
    type: 'meeting' | 'call' | 'note' | 'email';
    subject: string;
    description: string;
    scheduledAt?: string;
  }) => void;
}> = ({ mode, clientName, onClose, onSave }) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [location, setLocation] = useState('');
  const [subjectError, setSubjectError] = useState(false);
  const modeLabel = mode === 'meeting' ? 'New Event' : mode === 'note' ? 'New Post' : mode === 'call' ? 'Log a Call' : 'New Email';

  const handleSave = () => {
    const nextSubject = subject.trim();
    if (!nextSubject) {
      setSubjectError(true);
      return;
    }
    const details = [description.trim()];
    if (mode === 'meeting' && location.trim()) details.push(`Location: ${location.trim()}`);
    onSave({
      type: mode,
      subject: nextSubject,
      description: details.filter(Boolean).join('\n'),
      ...(scheduledAt ? { scheduledAt } : {}),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-16" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-600 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80 rounded-t-lg">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{modeLabel}</h3>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="Close">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              <span className="text-red-500">*</span> Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setSubjectError(false);
              }}
              className={`w-full px-3 py-2 text-sm border rounded ${subjectError ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100`}
              placeholder={`${modeLabel} for ${clientName}`}
              autoFocus
            />
            {subjectError ? <p className="text-xs text-red-600 mt-1">Subject is required.</p> : null}
          </div>
          {(mode === 'meeting' || mode === 'call') && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {mode === 'meeting' ? 'Start' : 'Date/Time'}
              </label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
          )}
          {mode === 'meeting' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                placeholder="Meeting location"
              />
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              {mode === 'note' ? 'Post' : mode === 'email' ? 'Body' : 'Comments'}
            </label>
            <textarea
              rows={mode === 'email' ? 6 : 4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              placeholder={mode === 'note' ? 'Share an update...' : 'Add details'}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="px-4 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// EXTRACTED TAB CONTENT COMPONENTS
// These can be rendered by DynamicTabRenderer's customTabContent prop
// ============================================

const ClientNotesPanel: React.FC<{
  clientId: string;
  initialText: string;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}> = ({ clientId, initialText, showToast }) => {
  const [text, setText] = useState(initialText);
  useEffect(() => setText(initialText), [initialText]);
  const saveNotes = useUpdateCreditBlockClientNotes(clientId);
  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-5 bg-slate-50/80 dark:bg-slate-900/40">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        disabled={!USE_GOBODO_API}
        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-60"
      />
      <StandardButton
        variant="primary"
        className="mt-3"
        disabled={!USE_GOBODO_API || saveNotes.isPending}
        onClick={() =>
          saveNotes.mutate(text, {
            onSuccess: () => showToast('Notes saved'),
            onError: () => showToast('Failed to save notes', 'error'),
          })
        }
      >
        Save notes
      </StandardButton>
    </div>
  );
};

type NotesAttachmentRow = {
  id: string;
  title: string;
  type: string;
  createdBy: string;
  lastModified: string;
};

type ApprovalHistoryRow = {
  id: string;
  stepName: string;
  date: string;
  status: string;
  assignedTo: string;
};

type ClientManagerRow = {
  id: string;
  clientManagerName: string;
  manager: string;
  role: string;
  active: string;
};

function formatDateTime(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

const CreditBlockNotesWorkspaceTabContent: React.FC<{
  client: CreditBlockClient;
  files: CreditBlockFile[];
  historyEntries: CreditBlockHistoryEntry[];
  subscriptions: CreditBlockProduct[];
  cases: ListViewRecord[];
  onSubscriptionClick?: (row: CreditBlockProduct) => void;
  onCaseClick?: (row: ListViewRecord) => void;
  renderNotesPanel: () => React.ReactNode;
}> = ({
  client,
  files,
  historyEntries,
  subscriptions,
  cases,
  onSubscriptionClick,
  onCaseClick,
  renderNotesPanel,
}) => {
  const notesAttachmentRows = useMemo<NotesAttachmentRow[]>(
    () =>
      (files ?? []).map((f) => ({
        id: String(f.id),
        title: f.fileName || 'Untitled',
        type: f.category === 'other' ? 'Attachment' : 'File',
        createdBy: f.uploadedBy || '—',
        lastModified: formatDateTime(f.uploadedDate),
      })),
    [files]
  );

  const approvalRows = useMemo<ApprovalHistoryRow[]>(
    () =>
      (historyEntries ?? []).map((h) => ({
        id: String(h.id),
        stepName: h.description || requestEntryTypeLabel(h.type),
        date: formatDateTime(h.processedDate || h.requestDate),
        status: h.status ? String(h.status).replace(/_/g, ' ') : '—',
        assignedTo: h.processedBy || h.requestedBy || '—',
      })),
    [historyEntries]
  );

  const managerRows = useMemo<ClientManagerRow[]>(() => {
    const rows: ClientManagerRow[] = [];
    const pushManager = (name: string | undefined, role: string, idx: number) => {
      if (!name || !name.trim()) return;
      rows.push({
        id: `cm-${idx}-${name}`,
        clientManagerName: `CM-${String(idx).padStart(4, '0')}`,
        manager: name,
        role,
        active: 'True',
      });
    };
    pushManager(client.accountOwner, 'Account Owner', 1);
    pushManager(client.accountManager, 'Account Manager', 2);
    pushManager(client.dayToDayContact, 'Day-to-Day Contact', 3);
    return rows;
  }, [client.accountOwner, client.accountManager, client.dayToDayContact]);

  const caseRows = useMemo(
    () =>
      (cases ?? []).map((c) => {
        const x = c as Record<string, unknown>;
        return {
          ...c,
          _caseNum:
            String(x.caseNumber ?? x.CaseNumber ?? x.case_num ?? '').trim() ||
            String(x.numericObjectId ?? x.id ?? '').trim(),
          _subject: String(x.subject ?? x.Subject__c ?? x.Subject ?? ''),
          _priority: String(x.priority ?? x.Priority__c ?? x.Priority ?? ''),
          _dateOpened: String(x.createdDate ?? x.CreatedDate ?? x.DateOpened__c ?? ''),
          _status: String(x.status ?? x.Status__c ?? x.Status ?? ''),
          _owner: String(x.owner ?? x.Owner__c ?? x.OwnerName ?? x.assignedTo ?? ''),
        };
      }),
    [cases]
  );

  const notesAttachmentColumns = useMemo<RelatedListColumn<NotesAttachmentRow>[]>(
    () => [
      { key: 'title', label: 'Title', isLink: true, width: '34%' },
      { key: 'type', label: 'Type' },
      { key: 'createdBy', label: 'Created By' },
      { key: 'lastModified', label: 'Last Modified' },
    ],
    []
  );

  const approvalColumns = useMemo<RelatedListColumn<ApprovalHistoryRow>[]>(
    () => [
      { key: 'stepName', label: 'Step Name', isLink: true, width: '36%' },
      { key: 'date', label: 'Date' },
      { key: 'status', label: 'Status' },
      { key: 'assignedTo', label: 'Assigned To' },
    ],
    []
  );

  const managerColumns = useMemo<RelatedListColumn<ClientManagerRow>[]>(
    () => [
      { key: 'clientManagerName', label: 'Client Manager Name', isLink: true, width: '24%' },
      { key: 'manager', label: 'Manager' },
      { key: 'role', label: 'Role' },
      { key: 'active', label: 'Active' },
    ],
    []
  );

  const subscriptionColumns = useMemo(() => getSubscriptionColumns(onSubscriptionClick), [onSubscriptionClick]);

  const notesCaseColumns = useMemo<RelatedListColumn<any>[]>(
    () => [
      {
        key: '_caseNum',
        label: 'Case',
        isLink: true,
        onLinkClick: (row) => onCaseClick?.(row),
      },
      { key: '_subject', label: 'Subject', width: '38%' },
      { key: '_priority', label: 'Priority' },
      { key: '_dateOpened', label: 'Date Opened', render: (row) => formatDateTime(row._dateOpened) },
      { key: '_status', label: 'Status' },
      { key: '_owner', label: 'Owner' },
    ],
    [onCaseClick]
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Notes (0)</h3>
        {renderNotesPanel()}
      </div>

      <SalesforceRelatedList<NotesAttachmentRow>
        title="Notes & Attachments"


        rows={notesAttachmentRows}
        columns={notesAttachmentColumns}
        pageSize={3}
        rowKey={(r) => r.id}
      />

      <SalesforceRelatedList<ApprovalHistoryRow>
        title="Approval History"


        rows={approvalRows}
        columns={approvalColumns}
        pageSize={6}
        rowKey={(r) => r.id}
      />

      <SalesforceRelatedList<CreditBlockProduct>
        title="Array Subscriptions"


        rows={subscriptions}
        columns={subscriptionColumns}
        pageSize={10}
        rowKey={(r) => r.id}
      />

      <SalesforceRelatedList<any>
        title="Cases"


        rows={caseRows}
        columns={notesCaseColumns}
        pageSize={10}
        rowKey={(r) => String(r.id)}
      />
    </div>
  );
};

/* ---- Salesforce-style column definitions for related lists ---- */

function getSubscriptionColumns(
  onSubscriptionClick?: (row: CreditBlockProduct) => void
): RelatedListColumn<CreditBlockProduct>[] {
  return [
    {
      key: 'id',
      label: 'Subscription',
      render: (row) => subscriptionDisplayLabel(row),
      sortValue: (row) => subscriptionDisplayLabel(row),
      isLink: true,
      onLinkClick: onSubscriptionClick,
      width: '25%',
    },
    {
      key: 'enrollmentDate',
      label: 'Start Date',
      render: (row) => formatSubDate(row.enrollmentDate),
    },
    {
      key: 'endDate',
      label: 'End Date',
      render: (row) => formatSubDate(row.endDate),
    },
    {
      key: 'status',
      label: 'Stage',
      render: (row) => subscriptionStageLabel(row),
      sortValue: (row) => subscriptionStageLabel(row),
    },
  ];
}

function getHistoryColumns(
  onHistoryClick?: (row: CreditBlockHistoryEntry) => void
): RelatedListColumn<CreditBlockHistoryEntry>[] {
  const bureauLabel = (raw: string): string => {
    const v = String(raw ?? '').trim().toLowerCase();
    if (!v) return '';
    if (v.includes('equifax')) return 'Equifax';
    if (v.includes('experian')) return 'Experian';
    if (v.includes('transunion') || v.includes('trans union')) return 'TransUnion';
    if (v.includes('chex')) return 'ChexSystems';
    return v.charAt(0).toUpperCase() + v.slice(1);
  };
  const bureauLogo = (raw: string): string | undefined => {
    const v = String(raw ?? '').trim().toLowerCase();
    if (!v) return undefined;
    if (v.includes('equifax')) return getBureauLogo('equifax') || undefined;
    if (v.includes('experian')) return getBureauLogo('experian') || undefined;
    if (v.includes('transunion') || v.includes('trans union')) return getBureauLogo('transunion') || undefined;
    if (v.includes('chex')) return getBureauLogo('chexsystems') || undefined;
    return undefined;
  };
  return [
  {
    key: 'id',
    label: 'CB History ID',
    render: (row) => historyDisplayLabel(row),
    sortValue: (row) => historyDisplayLabel(row),
    isLink: true,
    onLinkClick: onHistoryClick,
    width: '22%',
  },
  {
    key: 'bureau',
    label: 'Bureau Name',
    render: (row) => {
      const raw = String(row.bureau ?? '');
      const label = bureauLabel(raw) || '—';
      const logo = bureauLogo(raw);
      return (
        <span className="inline-flex items-center gap-2">
          {logo ? <img src={logo} alt="" className="h-4 w-4 object-contain" aria-hidden /> : null}
          <span>{label}</span>
        </span>
      );
    },
    sortValue: (row) => bureauLabel(String(row.bureau ?? '')),
  },
  {
    key: 'requestedBy',
    label: 'Record Created',
    render: (row) => formatSubDate(row.requestDate),
    sortValue: (row) => row.requestDate ?? '',
  },
  {
    key: 'type',
    label: 'Request Type',
    render: (row) => requestEntryTypeLabel(row.type),
    sortValue: (row) => requestEntryTypeLabel(row.type),
  },
  {
    key: 'startDate',
    label: 'Start Date',
    render: (row) => formatSubDate(row.startDate ?? row.requestDate),
  },
  {
    key: 'endDate',
    label: 'End Date',
    render: (row) => formatSubDate(row.endDate),
  },
  ];
}

/** Credit Block tab — Subscriptions + Histories related lists only (account detail moved to Details tab) */
const CreditBlockOverviewTabContent: React.FC<{
  products: CreditBlockProduct[];
  subscriptionsLoading: boolean;
  historyEntries: CreditBlockHistoryEntry[];
  historyLoading: boolean;
  historyError: unknown;
  onSettings?: () => void;
  onRefresh?: () => void;
  onSubscriptionClick?: (row: CreditBlockProduct) => void;
  onHistoryClick?: (row: CreditBlockHistoryEntry) => void;
}> = ({
  products,
  subscriptionsLoading,
  historyEntries,
  historyLoading,
  historyError,
  onSettings,
  onRefresh,
  onSubscriptionClick,
  onHistoryClick,
}) => {
  const [historyVisibleCount, setHistoryVisibleCount] = useState(10);
  const subscriptionColumns = useMemo(
    () => getSubscriptionColumns(onSubscriptionClick),
    [onSubscriptionClick]
  );
  const historyColumns = useMemo(
    () => getHistoryColumns(onHistoryClick),
    [onHistoryClick]
  );
  useEffect(() => {
    setHistoryVisibleCount(10);
  }, [historyEntries.length]);
  const visibleHistoryEntries = useMemo(
    () => historyEntries.slice(0, historyVisibleCount),
    [historyEntries, historyVisibleCount]
  );

  return (
    <div className="space-y-6">
      {/* Credit Block Subscriptions — Salesforce related list table */}
      <SalesforceRelatedList<CreditBlockProduct>
        title="Credit Block Subscriptions"


        rows={products}
        columns={subscriptionColumns}
        pageSize={10}
        loading={USE_GOBODO_API && subscriptionsLoading}
        rowKey={(r) => r.id}
        onSettings={onSettings}
        onRefresh={onRefresh}
      />

      {/* Credit Block Histories — Salesforce related list table */}
      <SalesforceRelatedList<CreditBlockHistoryEntry>
        title="Credit Block Histories"


        rows={visibleHistoryEntries}
        columns={historyColumns}
        pageSize={Math.max(10, visibleHistoryEntries.length)}
        loading={historyLoading}
        error={!!historyError}
        rowKey={(r) => r.id}
        onSettings={onSettings}
        onRefresh={onRefresh}
      />
      {!historyLoading && !historyError && historyEntries.length > historyVisibleCount && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setHistoryVisibleCount((prev) => prev + 10)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Load 10 more
          </button>
        </div>
      )}
    </div>
  );
};

const historyDisplayLabel = (row: CreditBlockHistoryEntry): string => {
  const explicit = normalizeDisplayToken((row as Record<string, unknown>).referenceNumber);
  if (explicit) return explicit;
  const fromDescription = normalizeDisplayToken(row.description).match(/\b[A-Z]+-\d+\b/i)?.[0];
  if (fromDescription) return fromDescription.toUpperCase();
  return normalizeDisplayToken(row.id) || '—';
};

// Bureaus Tab (complex custom UI)
const BureausTabContent: React.FC<{
  bureauCredentials: any;
  formData: any;
  setFormData: any;
  collapsedSections: Set<string>;
  toggleSection: (id: string) => void;
  editingField: string | null;
  setEditingField: (id: string | null) => void;
  tempEditValue: string;
  setTempEditValue: (v: string) => void;
  showPasswords: Record<string, boolean>;
  togglePasswordVisibility: (bureau: string) => void;
  handleSave: () => void;
  showToast: (msg: string, type?: 'success' | 'error') => void;
  updateBureau: any;
  bureausLoading: boolean;
  bureausError: any;
  subscriptionCount?: number;
  onBureauClick?: (bureauKey: string) => void;
}> = ({
  bureauCredentials, formData, setFormData, collapsedSections, toggleSection,
  editingField, setEditingField, tempEditValue, setTempEditValue,
  showPasswords, togglePasswordVisibility, handleSave, showToast, updateBureau,
  bureausLoading, bureausError, subscriptionCount = 0, onBureauClick,
}) => {
  const hasBureauData = Object.values(bureauCredentials ?? {}).some(
    (b: any) =>
      b &&
      (b.creditBureauId || b.pin || b.username || b.password || b.status || b.enrollmentDate)
  );

  return (
    <div className="space-y-6">
      {USE_GOBODO_API && subscriptionCount > 0 && !hasBureauData && !bureausLoading && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-900 dark:text-amber-100">
          No <strong>Credit_Bureaus</strong> rows are linked to this client in Gobodo (slots stay empty). Subscriptions load from{' '}
          <strong>CreditBlock</strong>; bureaus use <code className="text-xs">credit_bureaus_client_rel</code>. Verify RDS sync and
          that bureau records point at this Client id.
        </div>
      )}
      {USE_GOBODO_API && bureausError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-300">Failed to load bureau credentials.</p>
        </div>
      )}
      {bureausLoading && !hasBureauData ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading bureau credentials...</span>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Credit Bureaus</h3>
            <StandardButton variant="primary" leftIcon={<Save className="h-4 w-4" />} onClick={handleSave}>
              Save
            </StandardButton>
          </div>
          {BUREAUS.map((bureau) => {
            const cred = formData.bureauCredentials[bureau] ?? {};
            const isCollapsed = collapsedSections.has(`bureau-${bureau}`);
            const hasLogin = cred.pin || cred.username || cred.password;
            const logo = getBureauLogo(bureau);
            const fieldId = (key: string) => `${bureau}-${key}`;
            const isEditing = (key: string) => editingField === fieldId(key);
            const normalizeStageForEdit = (value: unknown): string => {
              const s = String(value ?? '').trim().toLowerCase();
              if (s === 'blocked') return 'locked';
              if (s === 'unblocked') return 'unlocked';
              return s === 'locked' || s === 'unlocked' ? s : '';
            };
            const startEdit = (key: string, value: string | undefined) => {
              setEditingField(fieldId(key));
              setTempEditValue(key === 'status' ? normalizeStageForEdit(value) : (value ?? ''));
            };
            const saveField = (key: string, value: string | boolean) => {
              const boolKeys = ['pinMailed', 'blockProcessCollection', 'unblockProcessCollection'];
              const v = boolKeys.includes(key) ? value === true || value === 'true' : value;
              const updates: Record<string, unknown> = { [key]: v };
              if (key === 'startDate' && typeof v === 'string') updates.enrollmentDate = v;
              if (key === 'endDate' && typeof v === 'string') updates.unblockDate = v;
              setFormData({
                ...formData,
                bureauCredentials: {
                  ...formData.bureauCredentials,
                  [bureau]: { ...cred, ...updates },
                },
              });
              setEditingField(null);
              if (USE_GOBODO_API && cred.creditBureauId) {
                updateBureau.mutate(
                  { bureau, creditBureauId: cred.creditBureauId, fieldKey: key, value: v },
                  {
                    onSuccess: () => showToast(`${BUREAU_LABELS[bureau]} ${key} saved`),
                    onError: () => showToast(`Failed to save ${key}`, 'error'),
                  }
                );
              }
            };
            const cancelEdit = () => setEditingField(null);
            const empty = <span className="text-gray-400 dark:text-gray-500 italic">—</span>;
            const EditableField = ({
              fkey,
              label,
              type = 'text',
              value,
              placeholder,
            }: {
              fkey: string;
              label: string;
              type?: string;
              value: string | undefined;
              placeholder?: string;
            }) => (
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</label>
                {isEditing(fkey) ? (
                  <div className="flex items-center gap-1">
                    {type === 'date' ? (
                      <input
                        type="date"
                        value={tempEditValue}
                        onChange={(e) => setTempEditValue(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        autoFocus
                      />
                    ) : type === 'select' ? (
                      <select
                        value={tempEditValue}
                        onChange={(e) => setTempEditValue(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        autoFocus
                      >
                        <option value="">—</option>
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                      </select>
                    ) : type === 'textarea' ? (
                      <textarea
                        value={tempEditValue}
                        onChange={(e) => setTempEditValue(e.target.value)}
                        placeholder={placeholder}
                        rows={4}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        autoFocus
                      />
                    ) : (
                      <input
                        type={type}
                        value={tempEditValue}
                        onChange={(e) => setTempEditValue(e.target.value)}
                        placeholder={placeholder}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        autoFocus
                      />
                    )}
                    <button type="button" onClick={() => saveField(fkey, tempEditValue)} className="p-1.5 text-emerald-600 hover:text-emerald-700" title="Save">
                      <Save className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={cancelEdit} className="p-1.5 text-gray-500 hover:text-gray-600" title="Cancel">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-2">
                    <p className="text-sm text-gray-900 dark:text-gray-100 flex-1 whitespace-pre-wrap">
                      {value ? (type === 'date' ? formatSubDate(value) : value) : empty}
                    </p>
                    <button
                      type="button"
                      onClick={() => startEdit(fkey, type === 'date' ? normalizeDateForInput(value) : value)}
                      className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Edit"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );

            return (
              <div key={bureau} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
                <button
                  type="button"
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors"
                  onClick={() => toggleSection(`bureau-${bureau}`)}
                >
                  <div className="flex items-center gap-3">
                    {logo && <img src={logo} alt="" className="h-7 w-7 object-contain" aria-hidden />}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{BUREAU_LABELS[bureau]}</h3>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        cred.status === 'locked'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                          : cred.status === 'unlocked'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {cred.status === 'locked' ? 'Blocked' : cred.status === 'unlocked' ? 'Unblocked' : hasLogin ? 'Configured' : 'Not configured'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {onBureauClick && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        onClick={(e) => { e.stopPropagation(); onBureauClick(bureau); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onBureauClick(bureau); } }}
                      >
                        View details
                      </span>
                    )}
                    {isCollapsed ? <ChevronDown className="h-5 w-5 text-gray-400" /> : <ChevronUp className="h-5 w-5 text-gray-400" />}
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="px-5 pb-5 pt-1 border-t border-gray-100 dark:border-gray-600/50 space-y-5">
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Information</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {cred.creditBureauId && (
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Credit Bureau ID</label>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{cred.creditBureauId}</p>
                          </div>
                        )}
                        <EditableField fkey="authority" label="Authority" type="select" value={cred.authority} />
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Stage</label>
                          {isEditing('status') ? (
                            <div className="flex items-center gap-1">
                              <select
                                value={tempEditValue}
                                onChange={(e) => setTempEditValue(e.target.value)}
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                autoFocus
                              >
                                <option value="">—None—</option>
                                <option value="unlocked">Unblocked</option>
                                <option value="locked">Blocked</option>
                              </select>
                              <button type="button" onClick={() => saveField('status', tempEditValue)} className="p-1.5 text-emerald-600 hover:text-emerald-700" title="Save">
                                <Save className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={cancelEdit} className="p-1.5 text-gray-500 hover:text-gray-600" title="Cancel">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <p className="text-sm text-gray-900 dark:text-gray-100 flex-1">
                                {cred.status === 'locked'
                                  ? 'Blocked'
                                  : cred.status === 'unlocked'
                                    ? 'Unblocked'
                                    : cred.status
                                      ? String(cred.status)
                                      : empty}
                              </p>
                              <button
                                type="button"
                                onClick={() => startEdit('status', cred.status)}
                                className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit"
                              >
                                <Edit3 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        <EditableField fkey="portalCreationDate" label="Portal Creation Date" type="date" value={cred.portalCreationDate} />
                        <EditableField fkey="accountAlias" label="Account alias" value={cred.accountAlias} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Unblock Date Information</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <EditableField fkey="startDate" label="Start Date" type="date" value={cred.startDate ?? cred.enrollmentDate} />
                        <EditableField fkey="endDate" label="End Date" type="date" value={cred.endDate ?? cred.unblockDate} />
                        <EditableField fkey="permUnblockDate" label="Perm Unblock Date" type="date" value={cred.permUnblockDate} />
                        <EditableField fkey="unblockRequestStatus" label="Request Status" value={cred.unblockRequestStatus} placeholder="pending, approved" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Login Information</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <EditableField fkey="username" label="User Name" value={cred.username} />
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Password</label>
                          {isEditing('password') ? (
                            <div className="flex items-center gap-1">
                              <input
                                type={showPasswords[bureau] ? 'text' : 'password'}
                                value={tempEditValue}
                                onChange={(e) => setTempEditValue(e.target.value)}
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                autoFocus
                              />
                              <button type="button" onClick={() => saveField('password', tempEditValue)} className="p-1.5 text-emerald-600">
                                <Save className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={cancelEdit} className="p-1.5 text-gray-500">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">
                                {cred.password ? (showPasswords[bureau] ? cred.password : '••••••••') : empty}
                              </span>
                              {cred.password && (
                                <button
                                  type="button"
                                  onClick={() => togglePasswordVisibility(bureau)}
                                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                                  title={showPasswords[bureau] ? 'Hide' : 'Show'}
                                >
                                  {showPasswords[bureau] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => startEdit('password', cred.password)}
                                className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit"
                              >
                                <Edit3 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">PIN(s) Mailed</label>
                          {isEditing('pinMailed') ? (
                            <div className="flex items-center gap-1">
                              <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={tempEditValue === 'true'} onChange={(e) => setTempEditValue(e.target.checked ? 'true' : '')} /> Mailed
                              </label>
                              <button type="button" onClick={() => saveField('pinMailed', tempEditValue === 'true')} className="p-1.5 text-emerald-600">
                                <Save className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={cancelEdit} className="p-1.5 text-gray-500">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <p className="text-sm text-gray-900 dark:text-gray-100">{cred.pinMailed ? 'Yes' : empty}</p>
                              <button type="button" onClick={() => startEdit('pinMailed', cred.pinMailed ? 'true' : '')} className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100">
                                <Edit3 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        <EditableField fkey="pin" label="PIN" value={cred.pin} />
                        <EditableField fkey="challengeQuestion" label="Challenge Question" value={cred.challengeQuestion} />
                        <EditableField fkey="challengeAnswer" label="4 Digit PIN" value={cred.challengeAnswer} />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Process & request</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Block process collection</label>
                          {isEditing('blockProcessCollection') ? (
                            <div className="flex items-center gap-1">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={tempEditValue === 'true'}
                                  onChange={(e) => setTempEditValue(e.target.checked ? 'true' : '')}
                                />
                                Yes
                              </label>
                              <button type="button" onClick={() => saveField('blockProcessCollection', tempEditValue === 'true')} className="p-1.5 text-emerald-600">
                                <Save className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={cancelEdit} className="p-1.5 text-gray-500">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <p className="text-sm text-gray-900 dark:text-gray-100">
                                {cred.blockProcessCollection === true ? 'Yes' : cred.blockProcessCollection === false ? 'No' : empty}
                              </p>
                              <button
                                type="button"
                                onClick={() =>
                                  startEdit('blockProcessCollection', cred.blockProcessCollection === true ? 'true' : '')
                                }
                                className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100"
                              >
                                <Edit3 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Unblock process collection</label>
                          {isEditing('unblockProcessCollection') ? (
                            <div className="flex items-center gap-1">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={tempEditValue === 'true'}
                                  onChange={(e) => setTempEditValue(e.target.checked ? 'true' : '')}
                                />
                                Yes
                              </label>
                              <button type="button" onClick={() => saveField('unblockProcessCollection', tempEditValue === 'true')} className="p-1.5 text-emerald-600">
                                <Save className="h-4 w-4" />
                              </button>
                              <button type="button" onClick={cancelEdit} className="p-1.5 text-gray-500">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <p className="text-sm text-gray-900 dark:text-gray-100">
                                {cred.unblockProcessCollection === true ? 'Yes' : cred.unblockProcessCollection === false ? 'No' : empty}
                              </p>
                              <button
                                type="button"
                                onClick={() =>
                                  startEdit('unblockProcessCollection', cred.unblockProcessCollection === true ? 'true' : '')
                                }
                                className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100"
                              >
                                <Edit3 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Request (long text)</label>
                        <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                          {cred.requestNotes || empty}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Notes</label>
                      {isEditing('notes') ? (
                        <div className="flex gap-1">
                          <textarea
                            value={tempEditValue}
                            onChange={(e) => setTempEditValue(e.target.value)}
                            rows={2}
                            placeholder="Add notes..."
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                            autoFocus
                          />
                          <button type="button" onClick={() => saveField('notes', tempEditValue)} className="p-1.5 text-emerald-600 self-start">
                            <Save className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={cancelEdit} className="p-1.5 text-gray-500 self-start">
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="group flex items-start gap-2">
                          <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap flex-1">{cred.notes || empty}</p>
                          <button type="button" onClick={() => startEdit('notes', cred.notes)} className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 flex-shrink-0">
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

function requestEntryTypeLabel(t: CreditBlockHistoryEntry['type']): string {
  switch (t) {
    case 'lock_request':
      return 'Lock';
    case 'unlock_request':
      return 'Unlock';
    case 'permanent_block':
      return 'Permanent block';
    case 'permanent_unblock':
      return 'Permanent Unblock';
    case 'extension':
      return 'Extension';
    case 'special_case':
      return 'Special Case';
    default:
      return t;
  }
}

// Lock Requests Tab
const LockRequestsTabContent: React.FC<{
  lockUnlockEntries: CreditBlockHistoryEntry[];
  filteredLockRequests: CreditBlockHistoryEntry[];
  requestTypeFilter:
    | 'all'
    | 'unlock_request'
    | 'lock_request'
    | 'permanent_block'
    | 'permanent_unblock'
    | 'extension'
    | 'special_case';
  setRequestTypeFilter: (
    v:
      | 'all'
      | 'unlock_request'
      | 'lock_request'
      | 'permanent_block'
      | 'permanent_unblock'
      | 'extension'
      | 'special_case'
  ) => void;
  historyLoading: boolean;
  historyError: any;
  showNewRequestModal: boolean;
  setShowNewRequestModal: (v: boolean) => void;
  selectedRequest: CreditBlockHistoryEntry | null;
  setSelectedRequest: (v: CreditBlockHistoryEntry | null) => void;
  newRequestForm: any;
  setNewRequestForm: any;
  createUnlockReq: any;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}> = ({
  lockUnlockEntries, filteredLockRequests, requestTypeFilter, setRequestTypeFilter,
  historyLoading, historyError, showNewRequestModal, setShowNewRequestModal,
  selectedRequest, setSelectedRequest
}) => (
  <div className="space-y-6">
    {historyError && (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-800 dark:text-red-300">Failed to load lock/unlock requests.</p>
      </div>
    )}
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lock / Unlock Requests</h3>
        <div className="flex items-center gap-3">
          <StandardSelect
            value={requestTypeFilter}
            onChange={(e) => setRequestTypeFilter(e.target.value as any)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'lock_request', label: 'Lock' },
              { value: 'unlock_request', label: 'Unlock' },
              { value: 'permanent_block', label: 'Permanent block' },
              { value: 'permanent_unblock', label: 'Permanent Unblock' },
              { value: 'extension', label: 'Extension' },
              { value: 'special_case', label: 'Special Case' },
            ]}
          />
          <StandardButton variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowNewRequestModal(true)}>
            New Request
          </StandardButton>
        </div>
      </div>
      <div className="p-6">
        {historyLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : lockUnlockEntries.length === 0 ? (
          <div className="text-center py-12">
            <Unlock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">No requests</h3>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bureau</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requested</th>
                </tr>
              </thead>
              <tbody>
                {filteredLockRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => setSelectedRequest(req)}>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                          req.type === 'unlock_request' || req.type === 'permanent_unblock'
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                            : req.type === 'extension'
                              ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
                              : req.type === 'special_case'
                                ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100'
                                : 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200'
                        }`}
                      >
                        {req.type === 'unlock_request' || req.type === 'permanent_unblock' ? (
                          <Unlock className="h-3 w-3" />
                        ) : req.type === 'extension' ? (
                          <Clock className="h-3 w-3" />
                        ) : req.type === 'special_case' ? (
                          <AlertTriangle className="h-3 w-3" />
                        ) : (
                          <Lock className="h-3 w-3" />
                        )}
                        {requestEntryTypeLabel(req.type)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">{req.bureau ?? '—'}</td>
                    <td className="px-4 py-3 text-sm">{req.status}</td>
                    <td className="px-4 py-3 text-sm">{req.requestDate ? new Date(req.requestDate).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  </div>
);

// Files Tab
const FilesTabContent: React.FC<{
  displayFiles: CreditBlockFile[];
  filesLoading: boolean;
  filesError: any;
  fileCategoryFilter: string;
  setFileCategoryFilter: (v: string) => void;
  fileCounts: any;
  dragActive: boolean;
  handleFileDrag: (e: React.DragEvent) => void;
  handleFileDrop: (e: React.DragEvent) => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilesAdded: (files: File[]) => void;
  handleDownloadFile: (file: CreditBlockFile) => void;
  showToast: (message: string, type?: 'success' | 'error') => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  isUploading: boolean;
  clientCRRecords: CreditReportRecord[];
  setSelectedCRRecord: (r: CreditReportRecord | null) => void;
  sfFiles: SalesforceFile[];
  sfFilesLoading: boolean;
}> = ({
  displayFiles, filesLoading, filesError, fileCategoryFilter, setFileCategoryFilter,
  fileCounts, dragActive, handleFileDrag, handleFileDrop, handleFileInputChange,
  handleFilesAdded, handleDownloadFile, showToast, fileInputRef, isUploading,
  clientCRRecords, setSelectedCRRecord, sfFiles, sfFilesLoading
}) => {
  const [viewingFile, setViewingFile] = useState<CreditBlockFile | null>(null);

  const handleFileMenuAction = async (action: string, file: CreditBlockFile) => {
    switch (action) {
      case 'view':
        setViewingFile(file);
        return;
      case 'download':
        handleDownloadFile(file);
        return;
      case 'share':
        if (navigator.share) {
          try {
            await navigator.share({
              title: file.fileName,
              text: `File: ${file.fileName}`,
              url: file.downloadUrl || undefined,
            });
            return;
          } catch {
            // If user cancels native share we silently fall back to copy.
          }
        }
        showToast('Share not available. Use Public Link.', 'error');
        return;
      case 'public-link': {
        const link = file.downloadUrl || `${window.location.origin}/files/${file.id}`;
        try {
          await navigator.clipboard.writeText(link);
          showToast('Public link copied');
        } catch {
          showToast('Could not copy public link', 'error');
        }
        return;
      }
      case 'view-details':
        showToast(`Viewing details for ${file.fileName}`);
        return;
      case 'upload-new-version':
        fileInputRef.current?.click();
        return;
      case 'edit-details':
        showToast(`Edit details coming soon for ${file.fileName}`);
        return;
      case 'delete':
        showToast('Delete action not wired yet', 'error');
        return;
      case 'remove-from-record':
        showToast('Remove from record action not wired yet', 'error');
        return;
      default:
        return;
    }
  };

  const FILE_ROW_ACTIONS = [
    { id: 'view', label: 'View' },
    { id: 'download', label: 'Download' },
    { id: 'share', label: 'Share' },
    { id: 'public-link', label: 'Public Link' },
    { id: 'view-details', label: 'View File Details' },
    { id: 'upload-new-version', label: 'Upload New Version' },
    { id: 'edit-details', label: 'Edit File Details' },
    { id: 'delete', label: 'Delete' },
    { id: 'remove-from-record', label: 'Remove from Record' },
  ];


  return (
  <div className="space-y-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Files & Documents</h3>
      <StandardButton
        variant="primary"
        leftIcon={<Upload className="h-4 w-4" />}
        onClick={() => fileInputRef.current?.click()}
        isLoading={isUploading}
      >
        Upload File
      </StandardButton>
    </div>
    <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileInputChange} />
    
    {filesError && (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-800">Failed to load files.</p>
      </div>
    )}

    {/* File list */}
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-visible">
      <div className="overflow-x-auto overflow-y-visible">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
        <thead className="bg-gray-50 dark:bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Uploaded</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filesLoading ? (
            <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">Loading...</td></tr>
          ) : displayFiles.length === 0 ? (
            <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">No files</td></tr>
          ) : (
            displayFiles.map((file, idx) => (
              <tr key={file.id || `file-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 text-sm font-medium">{file.fileName}</td>
                <td className="px-6 py-4 text-sm uppercase">{file.fileType || file.fileName?.split('.').pop() || '—'}</td>
                <td className="px-6 py-4 text-sm">{(file.fileSize / 1024).toFixed(1)} KB</td>
                <td className="px-6 py-4 text-sm">{new Date(file.uploadedDate).toLocaleDateString()}</td>
                <td className="px-6 py-4 relative overflow-visible">
                  <RowActionsMenu
                    actions={FILE_ROW_ACTIONS}
                    onAction={(action) => { void handleFileMenuAction(action, file); }}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
    <FileViewerModal file={viewingFile} onClose={() => setViewingFile(null)} />

    {/* Salesforce Files */}
    <div className="mt-8">
      <h4 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-3">Salesforce Files</h4>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Uploaded</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sfFilesLoading ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : sfFiles.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No files
                </td>
              </tr>
            ) : (
              sfFiles.map((sf) => (
                <SalesforceFileRow key={sf.contentVersionId} sf={sf} showToast={showToast} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
  );
};

const SF_FILE_ACTIONS = [
  { id: 'view', label: 'View' },
  { id: 'download', label: 'Download' },
  { id: 'share', label: 'Share' },
];

const FILE_EXTRACTOR_URL = process.env.NODE_ENV === 'development'
  ? '/file-extractor'
  : (process.env.REACT_APP_FILE_EXTRACTOR_URL ?? '').replace(/\/$/, '');

function SalesforceFileRow({ sf, showToast }: { sf: SalesforceFile; showToast: (msg: string, type?: 'success' | 'error') => void }) {
  const [busy, setBusy] = React.useState(false);
  const fileName = sf.extension ? `${sf.name}.${sf.extension}` : sf.name;

  const handleAction = async (action: string) => {
    if (action === 'view') {
      setBusy(true);
      try {
        const res = await fetch(FILE_EXTRACTOR_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contentVersionId: sf.contentVersionId }),
        });
        if (res.status === 404) throw new Error('The requested file could not be located or has not been synchronized yet. Please contact your administrator.');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      } catch (err: any) {
        showToast(err?.message ?? 'Could not open file', 'error');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (action === 'download') {
      setBusy(true);
      try {
        await downloadSalesforceFile(sf.contentVersionId, fileName);
      } catch (err: any) {
        showToast(err?.message ?? 'Download failed', 'error');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (action === 'share') {
      if (!navigator.share) {
        showToast('Sharing not supported in this browser', 'error');
        return;
      }
      setBusy(true);
      try {
        const res = await fetch(FILE_EXTRACTOR_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contentVersionId: sf.contentVersionId }),
        });
        if (res.status === 404) throw new Error('The requested file could not be located or has not been synchronized yet. Please contact your administrator.');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const file = new File([blob], fileName, { type: blob.type });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], title: fileName });
        } else {
          await navigator.share({ title: fileName, text: `File: ${fileName}` });
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          showToast(err?.message ?? 'Share failed', 'error');
        }
      } finally {
        setBusy(false);
      }
    }
  };

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{fileName}</td>
      <td className="px-6 py-4 text-sm uppercase text-gray-600 dark:text-gray-300">{sf.extension || '—'}</td>
      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
        {sf.size ? `${(sf.size / 1024).toFixed(1)} KB` : '—'}
      </td>
      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
        {sf.updatedAt ? new Date(sf.updatedAt).toLocaleDateString() : '—'}
      </td>
      <td className="px-6 py-4 relative overflow-visible">
        <RowActionsMenu actions={SF_FILE_ACTIONS} onAction={(action) => { void handleAction(action); }} loading={busy} />
      </td>
    </tr>
  );
}

// Credit Report Tab
function fmtCRDate(v: unknown): string {
  if (!v) return '';
  const s = String(v);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// Distinguish Ops-Hub-originated Array pulls from records that migrated in
// from Salesforce via the SF → RDS → Gobodo sync. Array pulls all stamp
// productCode = 'CR_ARRAY' on creation (arrayCreditReport/service.ts).
// Legacy SF records use 1B / 3B / various product codes from the bureau.
const isOpsHubArrayRecord = (row: CreditReportRecord): boolean =>
  String(row.productCode ?? '').toUpperCase() === 'CR_ARRAY';

// Pretty-print the Ops Hub Array approval state for the Status column. Falls
// back to the legacy SF Credit_Report_Status__c outcome ("Success" / "Error")
// for migrated records.
const REQUEST_STATUS_LABELS: Record<string, string> = {
  pending_approval: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
  recalled: 'Recalled',
  completed: 'Completed',
};
const formatStatusCell = (row: CreditReportRecord): string => {
  const rs = row.requestStatus;
  if (rs && REQUEST_STATUS_LABELS[rs]) return REQUEST_STATUS_LABELS[rs];
  if (rs) return rs;
  return row.status ?? '';
};

const CREDIT_REPORT_COLUMNS: RelatedListColumn<CreditReportRecord>[] = [
  {
    key: 'id',
    label: 'Credit Report Name',
    isLink: true,
    width: '20%',
  },
  {
    key: 'pullDate',
    label: 'Created Date',
    render: (row) => fmtCRDate(row.pullDate),
  },
  {
    key: 'source',
    label: 'Source',
    render: (row) => (isOpsHubArrayRecord(row) ? 'Ops Hub' : 'SF Legacy'),
    sortValue: (row) => (isOpsHubArrayRecord(row) ? 'Ops Hub' : 'SF Legacy'),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => formatStatusCell(row),
    sortValue: (row) => formatStatusCell(row),
  },
  {
    key: 'requestedBy',
    label: 'Requested by',
    render: (row) => row.systemInfo?.createdBy ?? '',
    sortValue: (row) => row.systemInfo?.createdBy ?? '',
  },
  {
    key: 'productCode',
    label: 'Product Code',
    render: (row) => row.productCode ?? '',
  },
];

const CreditReportTabContent: React.FC<{
  clientCRRecords: CreditReportRecord[];
  crRecordsLoading: boolean;
  crRecordsError: any;
  setSelectedCRRecord: (r: CreditReportRecord | null) => void;
  onNewCreditReport?: () => void;
}> = ({ clientCRRecords, crRecordsLoading, crRecordsError, setSelectedCRRecord, onNewCreditReport }) => {
  const columns = useMemo<RelatedListColumn<CreditReportRecord>[]>(
    () =>
      CREDIT_REPORT_COLUMNS.map((col) =>
        col.key === 'id'
          ? { ...col, onLinkClick: (row: CreditReportRecord) => setSelectedCRRecord(row) }
          : col
      ),
    [setSelectedCRRecord]
  );

  return (
    <SalesforceRelatedList<CreditReportRecord>
      title="Credit Reports"


      rows={clientCRRecords}
      columns={columns}
      pageSize={10}
      loading={crRecordsLoading}
      error={!!crRecordsError}
      rowKey={(r) => r.id}
      onNew={onNewCreditReport}
    />
  );
};

const EMPTY_REQUEST_FORM = {
  bureau: 'equifax' as const,
  requestType: 'unlock_request' as const,
  reason: '',
  startDate: '',
  endDate: '',
};

const NewRequestModal: React.FC<{
  showNewRequestModal: boolean;
  setShowNewRequestModal: (v: boolean) => void;
  newRequestForm: any;
  setNewRequestForm: any;
  createUnlockReq: any;
  showToast: (msg: string, type?: 'success' | 'error') => void;
}> = ({ showNewRequestModal, setShowNewRequestModal, newRequestForm, setNewRequestForm, createUnlockReq, showToast }) => {
  if (!showNewRequestModal) return null;

  const bureauSummary =
    newRequestForm.bureau === 'all_three'
      ? 'Equifax, Experian, TransUnion (3 records)'
      : BUREAU_LABELS[newRequestForm.bureau] ?? newRequestForm.bureau;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowNewRequestModal(false)}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New bureau request</h3>
          <button type="button" className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" onClick={() => setShowNewRequestModal(false)} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Request type</label>
            <select
              value={newRequestForm.requestType}
              onChange={(e) =>
                setNewRequestForm({
                  ...newRequestForm,
                  requestType: e.target.value as NewBureauRequestFormKind,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            >
              <option value="unlock_request">Unlock / Unblock</option>
              <option value="lock_request">Lock / Block</option>
              <option value="permanent_unblock">Permanent Unblock</option>
              <option value="extension">Extension</option>
              <option value="special_case">Special Case</option>
            </select>
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Bureau</span>
            <div className="flex flex-col gap-1" role="group" aria-label="Bureau">
              <button
                type="button"
                onClick={() => setNewRequestForm({ ...newRequestForm, bureau: 'all_three' })}
                className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
                  newRequestForm.bureau === 'all_three'
                    ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/25 dark:border-blue-400'
                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                }`}
              >
                <div className="min-w-0 text-left">
                  <span className="font-medium text-gray-900 dark:text-gray-100">All 3 bureaus</span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                    EQ · EX · TU
                  </span>
                </div>
                <div className="flex shrink-0 items-center justify-end gap-0.5" aria-hidden>
                  {CORE_THREE_BUREAUS.map((b) => (
                    <img
                      key={b}
                      src={BUREAU_LOGOS[b]}
                      alt=""
                      className="h-[0.949rem] w-[1.898rem] object-contain object-right opacity-90 dark:opacity-95"
                    />
                  ))}
                </div>
              </button>
              {BUREAUS.map((b) => {
                const src = getBureauLogo(b);
                const selected = newRequestForm.bureau === b;
                return (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setNewRequestForm({ ...newRequestForm, bureau: b })}
                    className={`flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm transition-colors ${
                      selected
                        ? 'border-blue-500 bg-blue-50/70 dark:bg-blue-950/25 dark:border-blue-400'
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    <span className="min-w-0 text-left font-medium text-gray-900 dark:text-gray-100">
                      {BUREAU_LABELS[b]}
                    </span>
                    <div className="flex h-[1.265rem] min-w-[3.45rem] shrink-0 items-center justify-end" aria-hidden>
                      {src ? (
                        <img
                          src={src}
                          alt=""
                          className="max-h-[0.949rem] max-w-[3.795rem] object-contain object-right"
                        />
                      ) : (
                        <Shield className="h-[0.949rem] w-[0.949rem] text-gray-500 dark:text-gray-400" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              ChexSystems is not included in &quot;all 3&quot;; pick it explicitly if needed.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start date</label>
              <input
                type="date"
                value={newRequestForm.startDate}
                onChange={(e) => setNewRequestForm({ ...newRequestForm, startDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End date</label>
              <input
                type="date"
                value={newRequestForm.endDate}
                onChange={(e) => setNewRequestForm({ ...newRequestForm, endDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason</label>
            <textarea
              value={newRequestForm.reason}
              onChange={(e) => setNewRequestForm({ ...newRequestForm, reason: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3">
          <StandardButton variant="ghost" onClick={() => setShowNewRequestModal(false)}>
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            disabled={createUnlockReq.isPending}
            onClick={() => {
              createUnlockReq.mutate(
                {
                  bureau: newRequestForm.bureau,
                  requestType: newRequestForm.requestType,
                  reason: newRequestForm.reason,
                  startDate: newRequestForm.startDate || undefined,
                  endDate: newRequestForm.endDate || undefined,
                },
                {
                  onSuccess: () => {
                    showToast(
                      newRequestForm.bureau === 'all_three'
                        ? 'Created 3 requests (Equifax, Experian, TransUnion)'
                        : `Request created for ${bureauSummary}`
                    );
                    setShowNewRequestModal(false);
                    setNewRequestForm({ ...EMPTY_REQUEST_FORM });
                  },
                  onError: () => showToast('Failed to create request', 'error'),
                }
              );
            }}
          >
            Submit
          </StandardButton>
        </div>
      </div>
    </div>
  );
};

export default CreditBlockClientDetail;
